import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  LayoutDashboard,
  CalendarDays,
  DollarSign,
  LogOut,
  Menu,
  X,
  TrendingUp,
  Clock,
  CheckCircle,
  Loader2,
  Star,
  Tag,
  Settings,
  Image,
  Trash2,
  Plus,
  FileText,
  Link,
  Package,
  HelpCircle,
  Mail,
  ChevronUp,
  ChevronDown,
  Eye,
  Download,
  CreditCard,
  Edit,
  Users,
  Wallet,
} from "lucide-react";
import axios from "axios";
import { format, parseISO, isSameDay } from "date-fns";
import { PaymentsTab } from "../components/PaymentsTab";
import { AddBookingDialog, EditDatesDialog } from "../components/BookingDialogs";
import { ChargesPanel } from "../components/ChargesPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [blockReason, setBlockReason] = useState("");
  
  // Booking details modal state
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDetails, setShowBookingDetails] = useState(false);
  
  // Email preview state
  const [emailPreview, setEmailPreview] = useState(null);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Fix pricing state
  const [showFixPricing, setShowFixPricing] = useState(false);
  const [fixPricingBooking, setFixPricingBooking] = useState(null);
  const [newNightlyRate, setNewNightlyRate] = useState("");
  
  // New state for additional features
  const [discountCodes, setDiscountCodes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [paymentSubmissions, setPaymentSubmissions] = useState([]);
  const [modificationRequests, setModificationRequests] = useState([]);
  const [selectedModRequest, setSelectedModRequest] = useState(null);
  const [modRejectReason, setModRejectReason] = useState("");
  const [modAdminNotes, setModAdminNotes] = useState("");
  const [addons, setAddons] = useState([]);
  const [houseRules, setHouseRules] = useState([]);
  const [icalFeeds, setIcalFeeds] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [settings, setSettings] = useState({
    base_price: 800,
    peak_price: 900,
    cleaning_fee: 350,
    minimum_nights: 4
  });

  // Minimum-night date overrides
  const [minNightOverrides, setMinNightOverrides] = useState([]);
  const [newOverride, setNewOverride] = useState({ start_date: "", end_date: "", min_nights: 3, label: "" });
  
  // Form state
  const [newCode, setNewCode] = useState({ code: "", discount: 10, description: "" });
  const [newReview, setNewReview] = useState({ guest_name: "", date: "", rating: 5, text: "", source: "Direct" });
  const [newMedia, setNewMedia] = useState({ url: "", caption: "", category: "general" });
  const [newAddon, setNewAddon] = useState({ name: "", description: "", price: 0, type: "service" });
  const [newRule, setNewRule] = useState("");
  const [newIcalFeed, setNewIcalFeed] = useState({ name: "", url: "" });
  const [newFaq, setNewFaq] = useState({ question: "", answer: "", category: "General" });
  
  // Edit booking dates state
  const [editingDates, setEditingDates] = useState(false);
  const [editCheckIn, setEditCheckIn] = useState("");
  const [editCheckOut, setEditCheckOut] = useState("");
  
  // FAQ categories
  const faqCategories = [
    "Getting to the Island",
    "Getting Around the Island", 
    "The Property",
    "Booking & Policies",
    "General"
  ];
  const [editingFaq, setEditingFaq] = useState(null);

  const adminName = localStorage.getItem("admin_name") || "Admin";

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
  });

  const fetchData = async () => {
    try {
      const [statsRes, bookingsRes, blockedRes, codesRes, reviewsRes, settingsRes, galleryRes, addonsRes, rulesRes, icalRes, faqsRes, emailsRes, paymentsRes, modRequestsRes, overridesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, getAuthHeaders()),
        axios.get(`${API}/admin/bookings`, getAuthHeaders()),
        axios.get(`${API}/blocked-dates`),
        axios.get(`${API}/admin/discount-codes`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/reviews`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/settings`, getAuthHeaders()).catch(() => ({ data: {} })),
        axios.get(`${API}/gallery`).catch(() => ({ data: [] })),
        axios.get(`${API}/addons`).catch(() => ({ data: [] })),
        axios.get(`${API}/house-rules`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/ical-feeds`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/faqs`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/email-templates`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/payment-submissions`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/modification-requests`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/min-night-overrides`, getAuthHeaders()).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      // Sort bookings by check-in date (chronological order) — copy first to avoid mutating in place
      const sortedBookings = [...(bookingsRes.data || [])].sort((a, b) => {
        return new Date(a.check_in) - new Date(b.check_in);
      });
      setBookings(sortedBookings);
      const blocked = blockedRes.data.blocked || [];
      const booked = blockedRes.data.booked || [];
      // Combine blocked and booked dates for calendar display
      const allBlockedDates = [
        ...blocked.map((d) => ({ ...d, date: parseISO(d.date) })),
        ...booked.map((d) => ({ ...d, date: parseISO(d.date), isBooked: true }))
      ];
      setBlockedDates(allBlockedDates);
      setDiscountCodes(codesRes.data || []);
      setReviews(reviewsRes.data || []);
      setGallery(galleryRes.data || []);
      setAddons(addonsRes.data || []);
      setHouseRules(rulesRes.data || []);
      setIcalFeeds(icalRes.data || []);
      setFaqs(faqsRes.data || []);
      setEmailTemplates(emailsRes.data || []);
      setPaymentSubmissions(paymentsRes.data || []);
      setModificationRequests(modRequestsRes.data || []);
      setMinNightOverrides(overridesRes.data || []);
      if (settingsRes.data?.pricing) {
        setSettings({
          base_price: settingsRes.data.pricing.base_price_per_night || 800,
          peak_price: settingsRes.data.pricing.peak_price_per_night || 900,
          cleaning_fee: settingsRes.data.pricing.cleaning_fee || 350,
          minimum_nights: settingsRes.data.pricing.minimum_nights || 4
        });
      }
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.clear();
        navigate("/admin");
      }
      console.error("Error fetching data:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    // Defer to a macrotask so state updates inside fetchData don't run
    // synchronously within the effect (avoids cascading renders).
    const timer = setTimeout(() => { fetchData(); }, 0);
    return () => clearTimeout(timer);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/admin");
  };

  const handleBlockDate = async () => {
    if (!selectedDate) return;
    try {
      await axios.post(
        `${API}/admin/blocked-dates`,
        { date: format(selectedDate, "yyyy-MM-dd"), reason: blockReason || "Blocked" },
        getAuthHeaders()
      );
      toast.success("Date blocked");
      setSelectedDate(null);
      setBlockReason("");
      fetchData();
    } catch (e) {
      toast.error("Failed to block date");
    }
  };

  const handleUnblockDate = async (date) => {
    // Dates that come from an active booking are NOT stored in blocked_dates and
    // cannot be removed by the Unblock button. They only clear when the booking is
    // cancelled. Detect this and guide the admin instead of silently doing nothing.
    const dateStr = format(date, "yyyy-MM-dd");
    const bookingBlock = blockedDates.find(
      (d) => format(d.date, "yyyy-MM-dd") === dateStr && d.isBooked && d.booking_id
    );
    if (bookingBlock) {
      const booking = bookings.find((b) => b.id === bookingBlock.booking_id);
      const guest = booking ? booking.guest_name : "a guest";
      toast.error(
        `This date is reserved by an active booking (${guest}). To free it, cancel that booking from the Bookings tab (set status to "Cancelled").`,
        { duration: 7000 }
      );
      return;
    }
    try {
      await axios.delete(
        `${API}/admin/blocked-dates/${dateStr}`,
        getAuthHeaders()
      );
      toast.success("Date unblocked");
      fetchData();
    } catch (e) {
      toast.error("Failed to unblock date");
    }
  };

  const handleUpdateStatus = async (bookingId, newStatus) => {
    if (newStatus === "cancelled") {
      const booking = bookings.find((b) => b.id === bookingId);
      const guest = booking ? booking.guest_name : "this booking";
      if (!window.confirm(
        `Cancel ${guest}'s booking?\n\nThis will release their reserved dates (and any cleaning-buffer days) back into inventory so they can be booked again.`
      )) {
        return;
      }
    }
    try {
      await axios.patch(
        `${API}/admin/bookings/${bookingId}?status=${newStatus}`,
        {},
        getAuthHeaders()
      );
      toast.success(
        newStatus === "cancelled"
          ? "Booking cancelled — dates released back into inventory"
          : "Booking updated"
      );
      fetchData();
    } catch (e) {
      toast.error("Failed to update booking");
    }
  };

  const handleMarkAsRefunded = async (bookingId) => {
    if (!window.confirm("Mark this booking as refunded? This action updates the payment status.")) {
      return;
    }
    try {
      await axios.patch(
        `${API}/admin/bookings/${bookingId}/payment-status?payment_status=refunded`,
        {},
        getAuthHeaders()
      );
      toast.success("Booking marked as refunded");
      setShowBookingDetails(false);
      fetchData();
    } catch (e) {
      toast.error("Failed to update payment status");
      console.error(e);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(
        `${API}/admin/settings/pricing?base_price=${settings.base_price}&peak_price=${settings.peak_price}&cleaning_fee=${settings.cleaning_fee}&minimum_nights=${settings.minimum_nights}`,
        {},
        getAuthHeaders()
      );
      toast.success("Settings saved!");
    } catch (e) {
      toast.error("Failed to save settings");
    }
  };

  const handleAddOverride = async () => {
    if (!newOverride.start_date || !newOverride.end_date) {
      toast.error("Please choose a start and end date");
      return;
    }
    if (newOverride.end_date < newOverride.start_date) {
      toast.error("End date must be on or after the start date");
      return;
    }
    try {
      await axios.post(`${API}/admin/min-night-overrides`, {
        start_date: newOverride.start_date,
        end_date: newOverride.end_date,
        min_nights: parseInt(newOverride.min_nights) || 1,
        label: newOverride.label || "",
      }, getAuthHeaders());
      toast.success("Minimum-night exception added");
      setNewOverride({ start_date: "", end_date: "", min_nights: 3, label: "" });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add exception");
    }
  };

  const handleDeleteOverride = async (id) => {
    try {
      await axios.delete(`${API}/admin/min-night-overrides/${id}`, getAuthHeaders());
      toast.success("Exception removed");
      fetchData();
    } catch (e) {
      toast.error("Failed to remove exception");
    }
  };

  const handleAddDiscountCode = async () => {
    if (!newCode.code) {
      toast.error("Please enter a code");
      return;
    }
    try {
      await axios.post(
        `${API}/admin/discount-codes?code=${newCode.code}&discount_percent=${newCode.discount / 100}&description=${newCode.description}`,
        {},
        getAuthHeaders()
      );
      toast.success(`Code ${newCode.code.toUpperCase()} created!`);
      setNewCode({ code: "", discount: 10, description: "" });
      fetchData();
    } catch (e) {
      toast.error("Failed to create code");
    }
  };

  const handleDeleteCode = async (code) => {
    try {
      await axios.delete(`${API}/admin/discount-codes/${code}`, getAuthHeaders());
      toast.success("Code deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete code");
    }
  };

  const handleAddReview = async () => {
    if (!newReview.guest_name || !newReview.text) {
      toast.error("Please fill in guest name and review text");
      return;
    }
    try {
      await axios.post(`${API}/admin/reviews`, newReview, getAuthHeaders());
      toast.success("Review added!");
      setNewReview({ guest_name: "", date: "", rating: 5, text: "", source: "Direct" });
      fetchData();
    } catch (e) {
      toast.error("Failed to add review");
    }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, getAuthHeaders());
      toast.success("Review deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete review");
    }
  };

  const handleAddMedia = async () => {
    if (!newMedia.url) {
      toast.error("Please enter a URL");
      return;
    }
    try {
      await axios.post(
        `${API}/admin/gallery?url=${encodeURIComponent(newMedia.url)}&caption=${encodeURIComponent(newMedia.caption)}&category=${newMedia.category}`,
        {},
        getAuthHeaders()
      );
      toast.success("Media added!");
      setNewMedia({ url: "", caption: "", category: "general" });
      fetchData();
    } catch (e) {
      toast.error("Failed to add media");
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    try {
      await axios.delete(`${API}/admin/gallery/${mediaId}`, getAuthHeaders());
      toast.success("Media deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete media");
    }
  };

  // Add-ons handlers
  const handleAddAddon = async () => {
    if (!newAddon.name || !newAddon.price) {
      toast.error("Please enter a name and price");
      return;
    }
    try {
      await axios.post(`${API}/admin/addons`, newAddon, getAuthHeaders());
      toast.success("Add-on created!");
      setNewAddon({ name: "", description: "", price: 0, type: "service" });
      fetchData();
    } catch (e) {
      toast.error("Failed to create add-on");
    }
  };

  const handleToggleAddon = async (addonId, currentActive) => {
    try {
      const addon = addons.find(a => a.id === addonId);
      await axios.put(`${API}/admin/addons/${addonId}`, { ...addon, active: !currentActive }, getAuthHeaders());
      toast.success(`Add-on ${!currentActive ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (e) {
      toast.error("Failed to update add-on");
    }
  };

  const handleDeleteAddon = async (addonId) => {
    try {
      await axios.delete(`${API}/admin/addons/${addonId}`, getAuthHeaders());
      toast.success("Add-on deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete add-on");
    }
  };

  const handleMoveAddon = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= addons.length) return;
    
    const newAddons = [...addons];
    [newAddons[index], newAddons[newIndex]] = [newAddons[newIndex], newAddons[index]];
    setAddons(newAddons);
    
    try {
      await axios.post(`${API}/admin/addons/reorder`, {
        addon_ids: newAddons.map(a => a.id)
      }, getAuthHeaders());
      toast.success("Order updated");
    } catch (e) {
      toast.error("Failed to update order");
      fetchData();
    }
  };

  // House rules handlers
  const handleAddRule = async () => {
    if (!newRule.trim()) {
      toast.error("Please enter a rule");
      return;
    }
    try {
      const updatedRules = [...houseRules, newRule.trim()];
      await axios.put(`${API}/admin/house-rules`, updatedRules, getAuthHeaders());
      toast.success("Rule added!");
      setNewRule("");
      fetchData();
    } catch (e) {
      toast.error("Failed to add rule");
    }
  };

  const handleDeleteRule = async (index) => {
    try {
      const updatedRules = houseRules.filter((_, i) => i !== index);
      await axios.put(`${API}/admin/house-rules`, updatedRules, getAuthHeaders());
      toast.success("Rule removed");
      fetchData();
    } catch (e) {
      toast.error("Failed to remove rule");
    }
  };

  // iCal feed handlers
  const handleAddIcalFeed = async () => {
    if (!newIcalFeed.name || !newIcalFeed.url) {
      toast.error("Please enter a name and URL");
      return;
    }
    try {
      await axios.post(`${API}/admin/ical-feeds?name=${encodeURIComponent(newIcalFeed.name)}&url=${encodeURIComponent(newIcalFeed.url)}`, {}, getAuthHeaders());
      toast.success("iCal feed added!");
      setNewIcalFeed({ name: "", url: "" });
      fetchData();
    } catch (e) {
      toast.error("Failed to add iCal feed");
    }
  };

  const handleSyncIcalFeed = async (feedId) => {
    try {
      const res = await axios.post(`${API}/admin/ical-feeds/${feedId}/sync`, {}, getAuthHeaders());
      toast.success(`Synced! ${res.data.events_imported} events imported.`);
      fetchData();
    } catch (e) {
      toast.error("Failed to sync feed");
    }
  };

  const handleDeleteIcalFeed = async (feedId) => {
    try {
      await axios.delete(`${API}/admin/ical-feeds/${feedId}`, getAuthHeaders());
      toast.success("Feed deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete feed");
    }
  };

  const handleMarkPaid = async (bookingId, paymentMethod) => {
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/mark-paid?payment_method=${paymentMethod}`, {}, getAuthHeaders());
      toast.success("Booking marked as paid!");
      fetchData();
    } catch (e) {
      toast.error("Failed to mark booking as paid");
    }
  };

  const handleResendConfirmation = async (bookingId, guestEmail) => {
    try {
      const res = await axios.post(`${API}/email/send-confirmation/${bookingId}`, {}, getAuthHeaders());
      if (res.data.success) {
        toast.success(`Confirmation email sent to ${guestEmail}`);
        setShowEmailPreview(false);
      } else {
        toast.error(res.data.message || "Email service error - please check configuration");
      }
    } catch (e) {
      toast.error("Failed to send confirmation email: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleViewBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setShowBookingDetails(true);
  };

  const handleCreateBuffer = async (bookingId) => {
    try {
      const res = await axios.get(
        `${API}/admin/create-buffer-for-booking/${bookingId}`,
        getAuthHeaders()
      );
      
      if (res.data.success) {
        toast.success("Buffer day created successfully!");
        fetchData(); // Refresh the calendar
      }
    } catch (error) {
      console.error("Error creating buffer:", error);
      toast.error(error.response?.data?.detail || "Failed to create buffer day");
    }
  };

  const [cleaningUp, setCleaningUp] = useState(false);
  
  const handleCleanupTestBookings = async () => {
    if (!window.confirm("This will delete ALL test bookings (keeping only real guest bookings). Are you sure?")) {
      return;
    }
    
    setCleaningUp(true);
    try {
      const res = await axios.get(`${API}/admin/cleanup-test-bookings`, getAuthHeaders());
      
      if (res.data.success) {
        toast.success(`Cleaned up ${res.data.deleted_count} test bookings!`);
        fetchData(); // Refresh all data
      }
    } catch (error) {
      console.error("Error cleaning up test bookings:", error);
      toast.error(error.response?.data?.detail || "Failed to clean up test bookings");
    } finally {
      setCleaningUp(false);
    }
  };

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [adminNotes, setAdminNotes] = useState("");

  const handleApprovePayment = async () => {
    if (!selectedSubmission) return;
    
    try {
      const res = await axios.post(
        `${API}/admin/payment-submissions/${selectedSubmission.id}/approve`,
        { admin_notes: adminNotes },
        getAuthHeaders()
      );
      toast.success(`Payment approved! New status: ${res.data.new_payment_status}`);
      setShowApproveModal(false);
      setSelectedSubmission(null);
      setAdminNotes("");
      fetchData(); // Refresh all data including bookings
    } catch (error) {
      console.error("Error approving payment:", error);
      toast.error(error.response?.data?.detail || "Failed to approve payment");
    }
  };

  const handleRejectPayment = async () => {
    if (!selectedSubmission || !rejectionReason) {
      toast.error("Please provide a rejection reason");
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/payment-submissions/${selectedSubmission.id}/reject`,
        { 
          rejection_reason: rejectionReason,
          admin_notes: adminNotes 
        },
        getAuthHeaders()
      );
      toast.success("Payment rejected and guest notified");
      setShowRejectModal(false);
      setSelectedSubmission(null);
      setRejectionReason("");
      setAdminNotes("");
      fetchData();
    } catch (error) {
      console.error("Error rejecting payment:", error);
      toast.error(error.response?.data?.detail || "Failed to reject payment");
    }
  };

  const getSubmissionBooking = (submissionId) => {
    const submission = paymentSubmissions.find(s => s.id === submissionId);
    if (!submission) return null;
    return bookings.find(b => b.id === submission.booking_id);
  };

  // Modification Request handlers
  const handleApproveModification = async () => {
    if (!selectedModRequest) return;
    
    try {
      const res = await axios.post(
        `${API}/admin/modification-requests/${selectedModRequest.id}/approve`,
        { admin_notes: modAdminNotes },
        getAuthHeaders()
      );
      toast.success(`Modification approved! New booking total: $${res.data.new_total?.toFixed(2)}`);
      setSelectedModRequest(null);
      setModAdminNotes("");
      fetchData(); // Refresh all data
    } catch (error) {
      console.error("Error approving modification:", error);
      toast.error(error.response?.data?.detail || "Failed to approve modification");
    }
  };

  const handleRejectModification = async () => {
    if (!selectedModRequest || !modRejectReason) {
      toast.error("Please provide a rejection reason");
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/modification-requests/${selectedModRequest.id}/reject`,
        { 
          reason: modRejectReason,
          admin_notes: modAdminNotes 
        },
        getAuthHeaders()
      );
      toast.success("Modification request rejected and guest notified");
      setSelectedModRequest(null);
      setModRejectReason("");
      setModAdminNotes("");
      fetchData();
    } catch (error) {
      console.error("Error rejecting modification:", error);
      toast.error(error.response?.data?.detail || "Failed to reject modification");
    }
  };

  const checkPaymentIssues = (submission) => {
    const booking = getSubmissionBooking(submission.id);
    if (!booking) return [];
    
    const issues = [];
    const totalPrice = booking.total_price || 0;
    const amountPaid = booking.amount_paid || 0;
    const remainingBalance = totalPrice - amountPaid;
    
    // Check if already fully paid
    if (booking.payment_status === 'paid') {
      issues.push({ type: 'error', message: 'Booking already paid in full' });
    }
    
    // Check for overpayment
    if (submission.amount > remainingBalance) {
      issues.push({ 
        type: 'warning', 
        message: `Overpayment: $${submission.amount.toFixed(2)} received, only $${remainingBalance.toFixed(2)} due` 
      });
    }
    
    // Check for amount mismatch (not matching expected deposits)
    const depositAmount = booking.deposit_amount || 0;
    const expectedSecond = (totalPrice - depositAmount) / 2;
    const expectedFinal = totalPrice - amountPaid;
    
    const isExactMatch = 
      Math.abs(submission.amount - depositAmount) < 1 ||
      Math.abs(submission.amount - expectedSecond) < 1 ||
      Math.abs(submission.amount - expectedFinal) < 1;
    
    if (!isExactMatch && submission.amount < remainingBalance) {
      issues.push({ 
        type: 'info', 
        message: `Amount doesn't match expected payments (Expected: $${expectedFinal.toFixed(2)} final or partial)` 
      });
    }
    
    return issues;
  };

  const handleUpdateBookingDates = async () => {
    if (!selectedBooking) return;
    
    try {
      const params = new URLSearchParams();
      if (editCheckIn) params.append("check_in", editCheckIn);
      if (editCheckOut) params.append("check_out", editCheckOut);
      
      const res = await axios.patch(
        `${API}/admin/bookings/${selectedBooking.id}/dates?${params.toString()}`,
        {},
        getAuthHeaders()
      );
      
      if (res.data.success) {
        toast.success(`Dates updated! Now: ${res.data.new_check_in} to ${res.data.new_check_out} (${res.data.total_nights} nights)`);
        setEditingDates(false);
        setShowBookingDetails(false);
        fetchData(); // Refresh bookings
      }
    } catch (error) {
      console.error("Error updating dates:", error);
      toast.error(error.response?.data?.detail || "Failed to update dates");
    }
  };

  const handlePreviewEmail = async (bookingId) => {
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/email/preview/${bookingId}`, getAuthHeaders());
      setEmailPreview(res.data);
      setShowEmailPreview(true);
    } catch (e) {
      toast.error("Failed to load email preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleFixPricing = async () => {
    if (!fixPricingBooking || !newNightlyRate) return;
    try {
      const res = await axios.post(
        `${API}/admin/bookings/${fixPricingBooking.id}/fix-pricing?nightly_rate=${newNightlyRate}`,
        {},
        getAuthHeaders()
      );
      toast.success("Pricing updated successfully!");
      setShowFixPricing(false);
      setFixPricingBooking(null);
      setNewNightlyRate("");
      fetchData(); // Refresh bookings
    } catch (e) {
      toast.error("Failed to fix pricing");
    }
  };

  const openFixPricing = (booking) => {
    setFixPricingBooking(booking);
    setNewNightlyRate(booking.nightly_rate || "");
    setShowFixPricing(true);
  };

  const isDateBlocked = (date) => {
    return blockedDates.some((b) => isSameDay(b.date, date) && !b.isBooked);
  };

  const isDateBooked = (date) => {
    return blockedDates.some((b) => isSameDay(b.date, date) && b.isBooked);
  };

  const navItems = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "bookings", label: "Bookings", icon: CalendarDays },
    { id: "calendar", label: "Calendar", icon: CalendarDays },
    { id: "payments", label: "Payments", icon: Wallet },
    { id: "payment-submissions", label: "Payment Submissions", icon: CreditCard },
    { id: "modification-requests", label: "Modification Requests", icon: Edit },
    { id: "pricing", label: "Pricing", icon: DollarSign },
    { id: "addons", label: "Add-ons", icon: Package },
    { id: "houserules", label: "House Rules", icon: FileText },
    { id: "discounts", label: "Discount Codes", icon: Tag },
    { id: "faqs", label: "FAQs", icon: HelpCircle },
    { id: "emails", label: "Email Templates", icon: Mail },
    { id: "reviews", label: "Reviews", icon: Star },
    { id: "media", label: "Media/Files", icon: Image },
    { id: "ical", label: "Calendar Sync", icon: Link },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#134E4A] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F4] flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-stone-200 transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-6 border-b border-stone-200">
          <h2 className="font-serif text-xl text-[#1C1917]">Chestnut Hill Inn</h2>
          <p className="text-sm text-[#78716C]">Admin Dashboard</p>
        </div>

        <nav className="p-4">
          {navItems.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => {
                setActiveTab(item.id);
                setSidebarOpen(false);
              }}
              className={`admin-nav-item w-full flex items-center gap-3 px-4 py-3 text-left mb-1 ${
                activeTab === item.id ? "active bg-[#e6f2f1] text-[#134E4A] border-r-2 border-[#134E4A]" : "text-[#78716C] hover:bg-stone-50"
              }`}
            >
              <item.icon size={18} />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-stone-200">
          <div className="flex items-center gap-3 mb-4 px-4">
            <div className="w-10 h-10 bg-[#134E4A] text-white rounded-full flex items-center justify-center font-medium">
              {adminName.charAt(0)}
            </div>
            <div>
              <p className="text-sm font-medium text-[#1C1917]">{adminName}</p>
              <p className="text-xs text-[#78716C]">Administrator</p>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-[#78716C] hover:text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut size={18} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64">
        {/* Header */}
        <header className="bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between sticky top-0 z-40">
          <button
            className="lg:hidden p-2 text-[#78716C]"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <h1 className="font-serif text-2xl text-[#1C1917] capitalize">
            {activeTab}
          </h1>
          <a
            href="/"
            target="_blank"
            className="text-sm text-[#134E4A] hover:underline"
          >
            View Website →
          </a>
        </header>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div data-testid="overview-content">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {[
                  { label: "Total Bookings", value: stats?.total_bookings || 0, icon: CalendarDays, color: "bg-blue-50 text-blue-600" },
                  { label: "Confirmed", value: stats?.confirmed || 0, icon: CheckCircle, color: "bg-green-50 text-green-600" },
                  { label: "Pending", value: stats?.pending || 0, icon: Clock, color: "bg-amber-50 text-amber-600" },
                  { label: "Revenue", value: `$${(stats?.total_revenue || 0).toLocaleString()}`, icon: TrendingUp, color: "bg-teal-50 text-teal-600" },
                ].map((stat, i) => (
                  <div key={i} className="stat-card bg-white border border-stone-200 p-6">
                    <div className={`w-10 h-10 ${stat.color} flex items-center justify-center mb-4`}>
                      <stat.icon size={20} />
                    </div>
                    <p className="text-[#78716C] text-sm mb-1">{stat.label}</p>
                    <p className="font-serif text-3xl text-[#1C1917]">{stat.value}</p>
                  </div>
                ))}
              </div>

              {/* Confirmed Bookings - Show at top */}
              {bookings.filter(b => b.status === "confirmed").length > 0 && (
                <div className="bg-green-50 border border-green-200">
                  <div className="p-6 border-b border-green-200 flex items-center justify-between">
                    <h2 className="font-serif text-xl text-green-800">✓ Confirmed Bookings</h2>
                    <span className="text-sm text-green-700">{bookings.filter(b => b.status === "confirmed").length} confirmed</span>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-green-100/50">
                          <TableHead>Guest</TableHead>
                          <TableHead>Dates</TableHead>
                          <TableHead>Guests</TableHead>
                          <TableHead>Total</TableHead>
                          <TableHead>Payment</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bookings.filter(b => b.status === "confirmed").map((booking) => (
                          <TableRow key={booking.id} className="bg-white">
                            <TableCell>
                              <div>
                                <p className="font-medium text-green-900">{booking.guest_name}</p>
                                <p className="text-sm text-green-700">{booking.guest_email}</p>
                                <p className="text-xs text-green-600">{booking.guest_phone}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{booking.check_in}</p>
                                <p className="text-sm text-[#78716C]">to {booking.check_out}</p>
                                <p className="text-xs text-green-600">{booking.total_nights} nights</p>
                              </div>
                            </TableCell>
                            <TableCell>{booking.guest_count}</TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">${booking.total_price?.toLocaleString()}</p>
                                <p className="text-xs text-green-600">Paid: ${(booking.amount_paid || booking.first_payment || 0).toLocaleString()}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 text-xs ${
                                booking.payment_status === "paid" ? "bg-green-200 text-green-800" : 
                                booking.payment_status === "deposit_paid" ? "bg-blue-100 text-blue-800" : 
                                booking.payment_status === "refunded" ? "bg-red-100 text-red-700 font-semibold" :
                                "bg-amber-100 text-amber-800"
                              }`}>
                                {booking.payment_status}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewBookingDetails(booking)}
                                  className="text-xs"
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  Details
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handlePreviewEmail(booking.id)}
                                  className="text-xs"
                                >
                                  <Mail className="w-3 h-3 mr-1" />
                                  Email
                                </Button>
                                {/* Show Create Buffer button for bookings with early check-in or late checkout */}
                                {booking.selected_addons?.some(addon => {
                                  const name = addon.name?.toLowerCase() || '';
                                  return name.includes('early check-in') || 
                                         name.includes('early check in') ||
                                         name.includes('late checkout') || 
                                         name.includes('late check-out');
                                }) && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleCreateBuffer(booking.id)}
                                    className="text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                                  >
                                    <Plus className="w-3 h-3 mr-1" />
                                    Buffer
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Pending Bookings */}
              {bookings.filter(b => b.status === "pending").length > 0 && (
                <div className="bg-amber-50 border border-amber-200">
                  <div className="p-6 border-b border-amber-200 flex items-center justify-between">
                    <h2 className="font-serif text-xl text-amber-800">⏳ Pending Bookings</h2>
                    <span className="text-sm text-amber-700">{bookings.filter(b => b.status === "pending").length} awaiting payment</span>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-amber-100/50">
                          <TableHead>Guest</TableHead>
                          <TableHead>Dates</TableHead>
                          <TableHead>Guests</TableHead>
                          <TableHead>Total</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bookings.filter(b => b.status === "pending").slice(0, 5).map((booking) => (
                          <TableRow key={booking.id} className="bg-white">
                            <TableCell>
                              <div>
                                <p className="font-medium">{booking.guest_name}</p>
                                <p className="text-sm text-[#78716C]">{booking.guest_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>{booking.check_in} → {booking.check_out}</TableCell>
                            <TableCell>{booking.guest_count}</TableCell>
                            <TableCell>${booking.total_price?.toLocaleString()}</TableCell>
                            <TableCell>
                              <span className="px-3 py-1 text-xs font-medium uppercase tracking-wider bg-amber-100 text-amber-700">
                                {booking.status}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Recent Activity - All bookings */}
              <div className="bg-white border border-stone-200">
                <div className="p-6 border-b border-stone-200 flex items-center justify-between">
                  <h2 className="font-serif text-xl text-[#1C1917]">Recent Activity</h2>
                  <Button
                    onClick={handleCleanupTestBookings}
                    disabled={cleaningUp}
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    {cleaningUp ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cleaning...</>
                    ) : (
                      <><Trash2 className="w-4 h-4 mr-2" /> Clean Up Test Bookings</>
                    )}
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Guest</TableHead>
                        <TableHead>Dates</TableHead>
                        <TableHead>Guests</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.slice(0, 5).map((booking) => (
                        <TableRow key={booking.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{booking.guest_name}</p>
                              <p className="text-sm text-[#78716C]">{booking.guest_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>{booking.check_in} → {booking.check_out}</TableCell>
                          <TableCell>{booking.guest_count}</TableCell>
                          <TableCell>${booking.total_price?.toLocaleString()}</TableCell>
                          <TableCell>
                            <span className={`px-3 py-1 text-xs font-medium uppercase tracking-wider ${
                              booking.status === "confirmed" ? "bg-green-100 text-green-700" :
                              booking.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"
                            }`}>
                              {booking.status}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          {/* Bookings Tab */}
          {activeTab === "bookings" && (
            <div data-testid="bookings-content" className="bg-white border border-stone-200">
              <div className="p-6 border-b border-stone-200 flex items-center justify-between">
                <h2 className="font-serif text-xl text-[#1C1917]">All Bookings</h2>
                <div className="flex items-center gap-4">
                  <p className="text-sm text-[#78716C]">{bookings.length} total bookings</p>
                  <AddBookingDialog getAuthHeaders={getAuthHeaders} onSaved={fetchData} />
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Guest</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead>Guests</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.map((booking) => (
                      <TableRow key={booking.id}>
                        <TableCell className="font-medium">{booking.guest_name}</TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm">{booking.guest_email}</p>
                            <p className="text-sm text-[#78716C]">{booking.guest_phone}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p>{booking.check_in}</p>
                            <p className="text-sm text-[#78716C]">to {booking.check_out}</p>
                          </div>
                        </TableCell>
                        <TableCell>{booking.guest_count}</TableCell>
                        <TableCell>${booking.total_price?.toLocaleString()}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 text-xs ${
                            booking.payment_status === "paid" ? "bg-green-100 text-green-700" : 
                            booking.payment_status === "refunded" ? "bg-red-100 text-red-700 font-semibold" :
                            "bg-amber-100 text-amber-700"
                          }`}>
                            {booking.payment_status}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`px-3 py-1 text-xs font-medium uppercase ${
                            booking.status === "confirmed" ? "bg-green-100 text-green-700" :
                            booking.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"
                          }`}>
                            {booking.status}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Select value={booking.status} onValueChange={(value) => handleUpdateStatus(booking.id, value)}>
                              <SelectTrigger className="w-28">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePreviewEmail(booking.id)}
                              className="text-xs whitespace-nowrap"
                              title="Preview and send confirmation email"
                            >
                              <Mail className="w-3 h-3 mr-1" />
                              Preview & Send
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openFixPricing(booking)}
                              className="text-xs whitespace-nowrap text-amber-600 border-amber-300 hover:bg-amber-50"
                              title="Fix incorrect pricing"
                            >
                              <DollarSign className="w-3 h-3 mr-1" />
                              Fix Price
                            </Button>
                            <EditDatesDialog booking={booking} getAuthHeaders={getAuthHeaders} onSaved={fetchData} />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Calendar Tab */}
          {activeTab === "calendar" && (
            <div data-testid="calendar-content" className="space-y-6">
              {/* Bulk Actions Bar */}
              <div className="bg-white border border-stone-200 p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <h3 className="font-semibold text-[#1C1917]">Bulk Actions:</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      if (!confirm("Clear ALL iCal-imported blocked dates? This will remove dates synced from Airbnb/VRBO.")) return;
                      try {
                        const res = await axios.delete(`${API}/admin/blocked-dates/clear-ical`, getAuthHeaders());
                        toast.success(`Cleared ${res.data.deleted} iCal blocked dates`);
                        fetchData();
                      } catch (e) {
                        toast.error("Error clearing iCal dates");
                      }
                    }}
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                  >
                    Clear All iCal Imports
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      if (!confirm("⚠️ WARNING: This will remove ALL blocked dates including manual blocks. Are you sure?")) return;
                      try {
                        const res = await axios.delete(`${API}/admin/blocked-dates/clear-all`, getAuthHeaders());
                        toast.success(`Cleared ${res.data.deleted} blocked dates`);
                        fetchData();
                      } catch (e) {
                        toast.error("Error clearing dates");
                      }
                    }}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    Clear ALL Blocked Dates
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/admin/blocked-dates/summary`, getAuthHeaders());
                        const summary = res.data;
                        let msg = `Total blocked: ${summary.total}\n\nBy source:\n`;
                        summary.by_source.forEach(s => { msg += `• ${s.source}: ${s.count} dates\n`; });
                        alert(msg);
                      } catch (e) {
                        toast.error("Error fetching summary");
                      }
                    }}
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  >
                    View Summary
                  </Button>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-8">
                {/* Block Dates Section */}
                <div className="bg-white border border-stone-200 p-6">
                  <h2 className="font-serif text-xl text-[#1C1917] mb-4">Block Dates</h2>
                  
                  {/* Date Range Picker */}
                  <div className="mb-6 p-4 bg-stone-50 border border-stone-200">
                    <h3 className="font-medium text-[#1C1917] mb-3">Block Date Range</h3>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label className="text-xs text-[#78716C]">Start Date</Label>
                        <Input 
                          type="date" 
                          id="bulk-start-date"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-[#78716C]">End Date</Label>
                        <Input 
                          type="date" 
                          id="bulk-end-date"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="mb-4">
                      <Label className="text-xs text-[#78716C]">Reason (optional)</Label>
                      <Input 
                        id="bulk-reason"
                        placeholder="e.g., Owner use, Maintenance"
                        className="mt-1"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={async () => {
                          const start = document.getElementById('bulk-start-date').value;
                          const end = document.getElementById('bulk-end-date').value;
                          const reason = document.getElementById('bulk-reason').value || 'Manually blocked';
                          if (!start || !end) { toast.error("Select both dates"); return; }
                          
                          // Generate date range without timezone issues
                          const dates = [];
                          const [startYear, startMonth, startDay] = start.split('-').map(Number);
                          const [endYear, endMonth, endDay] = end.split('-').map(Number);
                          let current = new Date(startYear, startMonth - 1, startDay); // Month is 0-indexed
                          const endDate = new Date(endYear, endMonth - 1, endDay);
                          
                          while (current <= endDate) {
                            const year = current.getFullYear();
                            const month = String(current.getMonth() + 1).padStart(2, '0');
                            const day = String(current.getDate()).padStart(2, '0');
                            dates.push(`${year}-${month}-${day}`);
                            current.setDate(current.getDate() + 1);
                          }
                          
                          try {
                            const res = await axios.post(`${API}/admin/blocked-dates/bulk-block`, { dates, reason }, getAuthHeaders());
                            toast.success(`Blocked ${res.data.blocked} dates`);
                            fetchData();
                          } catch (e) {
                            toast.error("Error blocking dates");
                          }
                        }}
                        className="flex-1 bg-[#134E4A] hover:bg-[#0f3d3a]"
                      >
                        Block Range
                      </Button>
                      <Button
                        variant="outline"
                        onClick={async () => {
                          const start = document.getElementById('bulk-start-date').value;
                          const end = document.getElementById('bulk-end-date').value;
                          if (!start || !end) { toast.error("Select both dates"); return; }
                          
                          // Generate date range without timezone issues
                          const dates = [];
                          const [startYear, startMonth, startDay] = start.split('-').map(Number);
                          const [endYear, endMonth, endDay] = end.split('-').map(Number);
                          let current = new Date(startYear, startMonth - 1, startDay);
                          const endDate = new Date(endYear, endMonth - 1, endDay);
                          
                          while (current <= endDate) {
                            const year = current.getFullYear();
                            const month = String(current.getMonth() + 1).padStart(2, '0');
                            const day = String(current.getDate()).padStart(2, '0');
                            dates.push(`${year}-${month}-${day}`);
                            current.setDate(current.getDate() + 1);
                          }
                          
                          try {
                            const res = await axios.post(`${API}/admin/blocked-dates/bulk-unblock`, { dates }, getAuthHeaders());
                            toast.success(`Unblocked ${res.data.unblocked} dates`);
                            fetchData();
                          } catch (e) {
                            toast.error("Error unblocking dates");
                          }
                        }}
                        className="flex-1"
                      >
                        Unblock Range
                      </Button>
                    </div>
                  </div>

                  {/* Single Date Calendar */}
                  <h3 className="font-medium text-[#1C1917] mb-3">Or Select Single Date</h3>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    modifiers={{ 
                      blocked: (date) => isDateBlocked(date),
                      booked: (date) => isDateBooked(date)
                    }}
                    modifiersStyles={{ 
                      blocked: { backgroundColor: "#fee2e2", color: "#991b1b" },
                      booked: { backgroundColor: "#bbf7d0", color: "#166534", fontWeight: "bold" }
                    }}
                    className="rounded-none border-0"
                  />
                  {selectedDate && (
                    <div className="mt-4 pt-4 border-t border-stone-200">
                      <p className="text-sm text-[#78716C] mb-3">Selected: {format(selectedDate, "MMMM d, yyyy")}</p>
                      <div className="flex gap-2">
                        <Button onClick={handleBlockDate} size="sm" className="flex-1 bg-[#134E4A] hover:bg-[#0f3d3a]">Block</Button>
                        <Button onClick={() => handleUnblockDate(selectedDate)} size="sm" variant="outline" className="flex-1">Unblock</Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Blocked Dates List */}
                <div className="bg-white border border-stone-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-serif text-xl text-[#1C1917]">Calendar Legend</h2>
                  </div>
                  
                  {/* Legend - Count reservations, not nights */}
                  <div className="flex gap-4 mb-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: "#bbf7d0" }}></div>
                      <span>Booked ({
                        // Count unique bookings by grouping consecutive booked dates
                        (() => {
                          const bookedDates = blockedDates.filter(d => d.isBooked).sort((a, b) => a.date - b.date);
                          if (bookedDates.length === 0) return 0;
                          let reservationCount = 1;
                          for (let i = 1; i < bookedDates.length; i++) {
                            const prevDate = bookedDates[i-1].date;
                            const currDate = bookedDates[i].date;
                            const daysDiff = Math.round((currDate - prevDate) / (1000 * 60 * 60 * 24));
                            // If more than 1 day gap, it's a new reservation
                            if (daysDiff > 1) reservationCount++;
                          }
                          return reservationCount;
                        })()
                      } reservations)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: "#fee2e2" }}></div>
                      <span>Blocked ({blockedDates.filter(d => !d.isBooked).length} days)</span>
                    </div>
                  </div>
                  
                  {/* Filter by source */}
                  <div className="mb-4">
                    <Select defaultValue="all" onValueChange={(v) => {
                      // This would filter the display - simplified for now
                      toast.info(`Filter: ${v}`);
                    }}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Filter by source" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Sources</SelectItem>
                        <SelectItem value="manual">Manual Only</SelectItem>
                        <SelectItem value="ical">iCal Imports Only</SelectItem>
                        <SelectItem value="booked">Bookings Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Group dates by booking_id for proper display */}
                  {blockedDates.length === 0 ? (
                    <p className="text-[#78716C]">No blocked dates</p>
                  ) : (
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {(() => {
                        // Group blocked dates by booking_id first
                        // BUT: Only group dates where isBooked is True or undefined (actual booking nights)
                        // Separate out buffer days (isBooked: False) even if they have booking_id
                        const bookingGroups = {};
                        const manualGroups = {}; // Group manual blocks by reason
                        const otherBlocked = [];
                        
                        for (const blocked of blockedDates) {
                          // Only group if it's a booking night (isBooked !== false)
                          // Buffer days have isBooked: false and should be shown separately
                          if (blocked.booking_id && blocked.isBooked !== false) {
                            if (!bookingGroups[blocked.booking_id]) {
                              bookingGroups[blocked.booking_id] = [];
                            }
                            bookingGroups[blocked.booking_id].push(blocked);
                          } else if (blocked.source === 'manual' && blocked.reason) {
                            // Group manual blocks by their reason text (for VRBO bookings, etc.)
                            if (!manualGroups[blocked.reason]) {
                              manualGroups[blocked.reason] = [];
                            }
                            manualGroups[blocked.reason].push(blocked);
                          } else {
                            // This includes: buffer days (isBooked: false) and non-booking dates
                            otherBlocked.push(blocked);
                          }
                        }
                        
                        // Create ranges from booking groups
                        const ranges = [];
                        
                        // Add booking ranges
                        for (const [bookingId, dates] of Object.entries(bookingGroups)) {
                          const sortedDates = dates.sort((a, b) => a.date - b.date);
                          if (sortedDates.length > 0) {
                            ranges.push({
                              start: sortedDates[0].date,
                              end: sortedDates[sortedDates.length - 1].date,
                              isBooked: true,
                              reason: sortedDates[0].reason,
                              source: sortedDates[0].source,
                              booking_id: bookingId
                            });
                          }
                        }
                        
                        // Add manual block ranges (grouped by reason)
                        for (const [reason, dates] of Object.entries(manualGroups)) {
                          const sortedDates = dates.sort((a, b) => a.date - b.date);
                          if (sortedDates.length > 0) {
                            ranges.push({
                              start: sortedDates[0].date,
                              end: sortedDates[sortedDates.length - 1].date,
                              isBooked: true, // Treat as booked for display purposes
                              reason: reason,
                              source: 'manual',
                              isManualBlock: true
                            });
                          }
                        }
                        
                        // Add other blocked dates (buffers)
                        for (const blocked of otherBlocked) {
                          ranges.push({
                            start: blocked.date,
                            end: blocked.date,
                            isBooked: blocked.isBooked,
                            reason: blocked.reason,
                            source: blocked.source
                          });
                        }
                        
                        // Sort ranges by start date
                        ranges.sort((a, b) => a.start - b.start);
                        
                        return ranges.slice(0, 30).map((range, i) => {
                          const isSingleDay = range.start.getTime() === range.end.getTime();
                          // For booked ranges, display end date is the last night + 1 day (checkout day)
                          // Backend returns last night stayed, so we add 1 day for display
                          const displayEnd = range.isBooked 
                            ? new Date(range.end.getTime() + 24 * 60 * 60 * 1000) 
                            : range.end;
                          const dateDisplay = isSingleDay 
                            ? format(range.start, "MMM d, yyyy")
                            : `${format(range.start, "MMM d")} – ${format(displayEnd, "MMM d, yyyy")}`;
                          // Calculate nights correctly: number of nights = end - start + 1
                          const nights = Math.round((range.end - range.start) / (1000 * 60 * 60 * 24)) + 1;
                          
                          return (
                            <div key={i} className={`flex items-center justify-between py-2 px-3 border border-stone-100 hover:bg-stone-50 ${range.isBooked ? 'bg-green-50' : ''}`}>
                              <div className="flex-1">
                                <p className="text-sm font-medium text-[#1C1917]">{dateDisplay}</p>
                                <p className="text-xs text-[#78716C]">
                                  {range.isBooked ? `${nights} night${nights > 1 ? 's' : ''}` : (range.reason || "No reason")}
                                  <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] ${range.isBooked ? 'bg-green-100 text-green-800' : 'bg-stone-100'}`}>
                                    {range.isBooked ? '✓ Booked' : range.source?.startsWith('ical') ? 'iCal' : range.source || 'manual'}
                                  </span>
                                </p>
                              </div>
                              {!range.isBooked && (
                                <Button variant="ghost" size="sm" onClick={() => {
                                  // Unblock entire range
                                  const dates = [];
                                  let current = new Date(range.start);
                                  while (current <= range.end) {
                                    dates.push(new Date(current));
                                    current.setDate(current.getDate() + 1);
                                  }
                                  dates.forEach(d => handleUnblockDate(d));
                                }} className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0">
                                  <Trash2 size={14} />
                                </Button>
                              )}
                            </div>
                          );
                        });
                      })()}
                      {blockedDates.length > 50 && (
                        <p className="text-center text-sm text-[#78716C] py-2">
                          Showing 50 of {blockedDates.length} dates. Use bulk actions to manage more.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Payments Tab */}
          {activeTab === "payments" && (
            <PaymentsTab getAuthHeaders={getAuthHeaders} />
          )}

          {/* Payment Submissions Tab */}
          {activeTab === "payment-submissions" && (
            <div className="bg-white border border-stone-200">
              <div className="p-6 border-b border-stone-200 flex items-center justify-between">
                <div>
                  <h2 className="font-serif text-xl text-[#1C1917]">Payment Submissions</h2>
                  <p className="text-sm text-[#78716C] mt-1">
                    {paymentSubmissions.filter(s => s.status === 'pending').length} pending • 
                    {paymentSubmissions.filter(s => s.status === 'approved').length} approved • 
                    {paymentSubmissions.filter(s => s.status === 'rejected').length} rejected
                  </p>
                </div>
              </div>
              <div className="p-6">
                {paymentSubmissions.length === 0 ? (
                  <p className="text-center text-[#78716C] py-8">No payment submissions yet</p>
                ) : (
                  <div className="space-y-4">
                    {paymentSubmissions.map((submission) => {
                      const booking = getSubmissionBooking(submission.id);
                      const issues = checkPaymentIssues(submission);
                      const remainingBalance = booking ? (booking.total_price - booking.amount_paid) : 0;
                      
                      return (
                        <div key={submission.id} className="border border-stone-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h3 className="font-semibold text-[#1C1917]">{submission.guest_name}</h3>
                              <p className="text-sm text-[#78716C]">{submission.guest_email}</p>
                              {booking && (
                                <p className="text-xs text-[#78716C] mt-1">
                                  Booking: {format(parseISO(booking.check_in), 'MMM d')} - {format(parseISO(booking.check_out), 'MMM d, yyyy')}
                                </p>
                              )}
                            </div>
                            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                              submission.status === 'approved' ? 'bg-green-100 text-green-700' :
                              submission.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-orange-100 text-orange-700'
                            }`}>
                              {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                            </div>
                          </div>
                          
                          {/* Edge Case Flags */}
                          {issues.length > 0 && (
                            <div className="mb-3 space-y-2">
                              {issues.map((issue, idx) => (
                                <div key={idx} className={`text-sm p-2 rounded border ${
                                  issue.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' :
                                  issue.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' :
                                  'bg-blue-50 border-blue-200 text-blue-700'
                                }`}>
                                  <strong>{issue.type === 'error' ? '🚨' : issue.type === 'warning' ? '⚠️' : 'ℹ️'}</strong> {issue.message}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                            <div>
                              <span className="text-[#78716C]">Payment Type:</span>
                              <p className="font-medium capitalize">{submission.payment_type}</p>
                            </div>
                            <div>
                              <span className="text-[#78716C]">Method:</span>
                              <p className="font-medium capitalize">{submission.payment_method}</p>
                            </div>
                            <div>
                              <span className="text-[#78716C]">Amount Submitted:</span>
                              <p className="font-medium text-lg">${submission.amount.toFixed(2)}</p>
                            </div>
                            <div>
                              <span className="text-[#78716C]">Submitted:</span>
                              <p className="font-medium">{new Date(submission.submitted_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                          
                          {booking && (
                            <div className="mb-3 p-3 bg-stone-50 rounded text-sm">
                              <div className="flex justify-between mb-1">
                                <span className="text-[#78716C]">Total Price:</span>
                                <span className="font-medium">${booking.total_price.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between mb-1">
                                <span className="text-[#78716C]">Already Paid:</span>
                                <span className="font-medium text-green-600">${booking.amount_paid.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between pt-1 border-t border-stone-200">
                                <span className="text-[#78716C] font-medium">Remaining Balance:</span>
                                <span className="font-bold">${remainingBalance.toFixed(2)}</span>
                              </div>
                            </div>
                          )}
                          
                          {submission.confirmation_number && (
                            <div className="mb-3 text-sm">
                              <span className="text-[#78716C]">Confirmation #:</span>
                              <p className="font-medium">{submission.confirmation_number}</p>
                            </div>
                          )}
                          
                          {submission.notes && (
                            <div className="mb-3 text-sm">
                              <span className="text-[#78716C]">Guest Notes:</span>
                              <p className="text-[#1C1917] bg-stone-50 p-2 rounded">{submission.notes}</p>
                            </div>
                          )}
                          
                          {submission.status === 'pending' && (
                            <div className="flex gap-2 mt-4">
                              <Button
                                onClick={() => {
                                  setSelectedSubmission(submission);
                                  setShowApproveModal(true);
                                }}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                size="sm"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve & Send Email
                              </Button>
                              <Button
                                onClick={() => {
                                  setSelectedSubmission(submission);
                                  setShowRejectModal(true);
                                }}
                                variant="outline"
                                size="sm"
                                className="border-red-300 text-red-700 hover:bg-red-50"
                              >
                                <X className="w-4 h-4 mr-1" />
                                Reject & Notify
                              </Button>
                            </div>
                          )}
                          
                          {submission.status !== 'pending' && submission.reviewed_at && (
                            <div className="mt-3 text-sm text-[#78716C]">
                              Reviewed on {new Date(submission.reviewed_at).toLocaleString()}
                            </div>
                          )}
                          
                          {submission.admin_notes && (
                            <div className="mt-3 text-sm bg-stone-50 p-2 rounded">
                              <span className="text-[#78716C]">Admin Notes:</span>
                              <p className="text-[#1C1917]">{submission.admin_notes}</p>
                            </div>
                          )}
                          
                          {submission.rejection_reason && (
                            <div className="mt-3 text-sm bg-red-50 p-2 rounded border border-red-200">
                              <span className="text-red-700 font-medium">Rejection Reason:</span>
                              <p className="text-red-900">{submission.rejection_reason}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Modification Requests Tab */}
          {activeTab === "modification-requests" && (
            <div data-testid="modification-requests-content">
              <div className="bg-white border border-stone-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-serif text-xl text-[#1C1917]">Modification Requests</h2>
                  <div className="flex gap-2">
                    <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">
                      {modificationRequests.filter(r => r.status === 'pending').length} Pending
                    </span>
                  </div>
                </div>
                
                {modificationRequests.length === 0 ? (
                  <p className="text-center text-[#78716C] py-8">No modification requests</p>
                ) : (
                  <div className="space-y-4">
                    {modificationRequests.map((request) => {
                      const booking = bookings.find(b => b.id === request.booking_id);
                      const isPending = request.status === 'pending';
                      
                      return (
                        <div 
                          key={request.id} 
                          className={`border rounded-lg p-4 ${
                            isPending ? 'border-amber-300 bg-amber-50' : 
                            request.status === 'approved' ? 'border-green-200 bg-green-50' : 
                            'border-red-200 bg-red-50'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h4 className="font-semibold text-lg">{request.guest_name}</h4>
                              <p className="text-sm text-[#78716C]">
                                {booking ? `${booking.check_in} to ${booking.check_out}` : 'Booking not found'}
                              </p>
                              <p className="text-xs text-[#78716C] mt-1">
                                Submitted: {format(parseISO(request.submitted_at), "MMM d, yyyy 'at' h:mm a")}
                              </p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              isPending ? 'bg-amber-200 text-amber-900' :
                              request.status === 'approved' ? 'bg-green-200 text-green-900' :
                              'bg-red-200 text-red-900'
                            }`}>
                              {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                            </span>
                          </div>
                          
                          {/* Changes Requested — itemized */}
                          {request.changes && request.changes.length > 0 ? (
                            <div className="bg-white/60 rounded p-3 mb-4" data-testid={`mod-changes-${request.id}`}>
                              <p className="text-sm font-medium text-[#78716C] mb-2">Reservation Changes Requested</p>
                              <div className="space-y-1">
                                {request.changes.map((c, i) => (
                                  <div key={i} className="flex justify-between text-sm">
                                    <span className="text-[#78716C]">{c.label}</span>
                                    <span className="font-medium text-[#1C1917]">
                                      {c.from} → <span className="text-[#134E4A]">{c.to}</span>
                                      {c.note ? <span className="text-green-700"> · {c.note}</span> : null}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="grid md:grid-cols-2 gap-4 mb-4">
                              <div className="bg-white/50 p-3 rounded">
                                <p className="text-sm font-medium text-[#78716C] mb-2">Current</p>
                                <p><strong>Guests:</strong> {request.current_guest_count}</p>
                                <p><strong>Add-ons:</strong> {request.current_addons?.length || 0} items (${request.current_addons_total?.toFixed(2)})</p>
                                {request.current_addons?.map((addon, i) => (
                                  <p key={i} className="text-sm text-[#78716C] ml-4">• {addon.name}</p>
                                ))}
                              </div>
                              <div className="bg-white/50 p-3 rounded">
                                <p className="text-sm font-medium text-[#78716C] mb-2">Requested</p>
                                <p><strong>Guests:</strong> {request.requested_guest_count}</p>
                                <p><strong>Add-ons:</strong> {request.requested_addons?.length || 0} items (${request.requested_addons_total?.toFixed(2)})</p>
                                {request.requested_addons?.map((addon, i) => (
                                  <p key={i} className="text-sm text-[#78716C] ml-4">• {addon.name}</p>
                                ))}
                              </div>
                            </div>
                          )}
                          {request.notes ? (
                            <div className="bg-amber-100/60 rounded p-3 mb-4 text-sm">
                              <strong>Guest Notes:</strong> {request.notes}
                            </div>
                          ) : null}
                          
                          <div className="flex items-center justify-between border-t pt-3">
                            <div>
                              <span className="text-sm text-[#78716C]">Price Adjustment: </span>
                              <span className={`font-bold ${
                                request.price_difference > 0 ? 'text-amber-600' : 
                                request.price_difference < 0 ? 'text-green-600' : ''
                              }`}>
                                {request.price_difference > 0 ? '+' : ''}${request.price_difference?.toFixed(2)}
                              </span>
                            </div>
                            
                            {isPending && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-red-300 text-red-700 hover:bg-red-50"
                                  onClick={() => {
                                    setSelectedModRequest(request);
                                    setModRejectReason("");
                                    setModAdminNotes("");
                                  }}
                                >
                                  Review / Reject
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => {
                                    setSelectedModRequest(request);
                                    handleApproveModification();
                                  }}
                                >
                                  Approve
                                </Button>
                              </div>
                            )}
                          </div>
                          
                          {request.notes && (
                            <div className="mt-3 text-sm">
                              <span className="text-[#78716C]">Guest Notes: </span>
                              <span>{request.notes}</span>
                            </div>
                          )}
                          
                          {request.rejection_reason && (
                            <div className="mt-3 p-2 bg-red-100 rounded text-sm">
                              <strong>Rejection Reason:</strong> {request.rejection_reason}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Modification Request Reject Modal */}
          {selectedModRequest && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <h3 className="text-lg font-semibold mb-4">Review Modification Request</h3>
                <p className="text-sm text-[#78716C] mb-4">
                  Request from <strong>{selectedModRequest.guest_name}</strong> for guest count and add-on changes.
                </p>
                
                <div className="mb-4 p-3 bg-stone-50 rounded text-sm">
                  <p><strong>Price Adjustment:</strong> ${selectedModRequest.price_difference > 0 ? '+' : ''}{selectedModRequest.price_difference?.toFixed(2)}</p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label>Rejection Reason (required to reject)</Label>
                    <Textarea
                      value={modRejectReason}
                      onChange={(e) => setModRejectReason(e.target.value)}
                      placeholder="e.g., Cannot accommodate guest count change at this time..."
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Admin Notes (optional)</Label>
                    <Textarea
                      value={modAdminNotes}
                      onChange={(e) => setModAdminNotes(e.target.value)}
                      placeholder="Internal notes or message to guest..."
                      rows={2}
                    />
                  </div>
                </div>
                
                <div className="flex gap-2 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedModRequest(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    onClick={handleRejectModification}
                    disabled={!modRejectReason}
                  >
                    Reject Request
                  </Button>
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    onClick={handleApproveModification}
                  >
                    Approve Request
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Pricing Tab */}
          {activeTab === "pricing" && (
            <div data-testid="pricing-content" className="max-w-2xl">
              <div className="bg-white border border-stone-200 p-6">
                <h2 className="font-serif text-xl text-[#1C1917] mb-6">Pricing Settings</h2>
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <Label className="text-sm text-[#78716C] mb-2 block">Base Price ($/night)</Label>
                      <Input type="number" value={settings.base_price} onChange={(e) => setSettings({...settings, base_price: parseFloat(e.target.value)})} />
                      <p className="text-xs text-[#78716C] mt-1">Sept - June rate</p>
                    </div>
                    <div>
                      <Label className="text-sm text-[#78716C] mb-2 block">Peak Price ($/night)</Label>
                      <Input type="number" value={settings.peak_price} onChange={(e) => setSettings({...settings, peak_price: parseFloat(e.target.value)})} />
                      <p className="text-xs text-[#78716C] mt-1">July & August rate</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <Label className="text-sm text-[#78716C] mb-2 block">Cleaning Fee ($)</Label>
                      <Input type="number" value={settings.cleaning_fee} onChange={(e) => setSettings({...settings, cleaning_fee: parseFloat(e.target.value)})} />
                    </div>
                    <div>
                      <Label className="text-sm text-[#78716C] mb-2 block">Minimum Nights</Label>
                      <Input type="number" value={settings.minimum_nights} onChange={(e) => setSettings({...settings, minimum_nights: parseInt(e.target.value)})} />
                    </div>
                  </div>
                  <div className="pt-4 border-t border-stone-200">
                    <p className="text-sm text-[#78716C] mb-4">Maine rental tax (9%) is automatically added to all bookings.</p>
                    <Button onClick={handleSaveSettings} className="bg-[#134E4A] hover:bg-[#0f3d3a]">Save Settings</Button>
                  </div>
                </div>
              </div>

              {/* Minimum-Night Exceptions */}
              <div className="bg-white border border-stone-200 p-6 mt-6" data-testid="min-night-overrides-card">
                <h2 className="font-serif text-xl text-[#1C1917] mb-2">Minimum-Night Exceptions</h2>
                <p className="text-sm text-[#78716C] mb-6">
                  Allow shorter stays for specific dates. Any check-in date within a range below uses that minimum
                  instead of the seasonal rule (summer is 4 nights). Example: allow a 3-night stay Aug 9–12.
                </p>

                {minNightOverrides.length > 0 ? (
                  <div className="space-y-2 mb-6" data-testid="override-list">
                    {minNightOverrides.map((o) => (
                      <div key={o.id} className="flex items-center justify-between bg-stone-50 border border-stone-200 px-4 py-3" data-testid={`override-row-${o.id}`}>
                        <div className="text-sm">
                          <span className="font-medium text-[#1C1917]">{o.start_date} → {o.end_date}</span>
                          <span className="text-[#78716C]"> · {o.min_nights}-night minimum</span>
                          {o.label ? <span className="text-[#78716C]"> · {o.label}</span> : null}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteOverride(o.id)}
                          data-testid={`delete-override-${o.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#78716C] italic mb-6">No exceptions yet.</p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
                  <div>
                    <Label className="text-sm text-[#78716C] mb-2 block">Start (check-in from)</Label>
                    <Input type="date" value={newOverride.start_date} onChange={(e) => setNewOverride({ ...newOverride, start_date: e.target.value })} data-testid="override-start-date" />
                  </div>
                  <div>
                    <Label className="text-sm text-[#78716C] mb-2 block">End (check-in until)</Label>
                    <Input type="date" value={newOverride.end_date} onChange={(e) => setNewOverride({ ...newOverride, end_date: e.target.value })} data-testid="override-end-date" />
                  </div>
                  <div>
                    <Label className="text-sm text-[#78716C] mb-2 block">Min Nights</Label>
                    <Input type="number" min="1" value={newOverride.min_nights} onChange={(e) => setNewOverride({ ...newOverride, min_nights: e.target.value })} data-testid="override-min-nights" />
                  </div>
                  <div>
                    <Button onClick={handleAddOverride} className="w-full bg-[#134E4A] hover:bg-[#0f3d3a]" data-testid="add-override-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                </div>
                <div className="mt-4">
                  <Label className="text-sm text-[#78716C] mb-2 block">Label (optional)</Label>
                  <Input value={newOverride.label} onChange={(e) => setNewOverride({ ...newOverride, label: e.target.value })} placeholder="e.g. Christine — Aug stay" data-testid="override-label" />
                </div>
              </div>
            </div>
          )}

          {/* Discount Codes Tab */}
          {activeTab === "discounts" && (
            <div data-testid="discounts-content" className="grid lg:grid-cols-2 gap-8">
              <div className="bg-white border border-stone-200 p-6">
                <h2 className="font-serif text-xl text-[#1C1917] mb-6">Create Discount Code</h2>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-[#78716C] mb-2 block">Code</Label>
                    <Input value={newCode.code} onChange={(e) => setNewCode({...newCode, code: e.target.value.toUpperCase()})} placeholder="FRIENDS10" />
                  </div>
                  <div>
                    <Label className="text-sm text-[#78716C] mb-2 block">Discount (%)</Label>
                    <Input type="number" value={newCode.discount} onChange={(e) => setNewCode({...newCode, discount: parseInt(e.target.value)})} />
                  </div>
                  <div>
                    <Label className="text-sm text-[#78716C] mb-2 block">Description (optional)</Label>
                    <Input value={newCode.description} onChange={(e) => setNewCode({...newCode, description: e.target.value})} placeholder="Friends & family discount" />
                  </div>
                  <Button onClick={handleAddDiscountCode} className="w-full bg-[#134E4A] hover:bg-[#0f3d3a]">
                    <Plus size={16} className="mr-2" /> Create Code
                  </Button>
                </div>
              </div>
              <div className="bg-white border border-stone-200 p-6">
                <h2 className="font-serif text-xl text-[#1C1917] mb-6">Active Codes</h2>
                {discountCodes.length === 0 ? (
                  <p className="text-[#78716C]">No discount codes yet. Create one to share with friends & family!</p>
                ) : (
                  <div className="space-y-3">
                    {discountCodes.map((code, i) => (
                      <div key={i} className="flex items-center justify-between py-3 border-b border-stone-100">
                        <div>
                          <p className="font-mono font-bold text-[#134E4A]">{code.code}</p>
                          <p className="text-sm text-[#78716C]">{(code.discount_percent * 100).toFixed(0)}% off • {code.description || "No description"}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteCode(code.code)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reviews Tab */}
          {activeTab === "reviews" && (
            <div data-testid="reviews-content" className="grid lg:grid-cols-2 gap-8">
              <div className="bg-white border border-stone-200 p-6">
                <h2 className="font-serif text-xl text-[#1C1917] mb-6">Add Review</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-[#78716C] mb-2 block">Guest Name</Label>
                      <Input value={newReview.guest_name} onChange={(e) => setNewReview({...newReview, guest_name: e.target.value})} placeholder="Sarah M." />
                    </div>
                    <div>
                      <Label className="text-sm text-[#78716C] mb-2 block">Date</Label>
                      <Input value={newReview.date} onChange={(e) => setNewReview({...newReview, date: e.target.value})} placeholder="August 2024" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-[#78716C] mb-2 block">Rating (1-5)</Label>
                      <Select value={String(newReview.rating)} onValueChange={(v) => setNewReview({...newReview, rating: parseInt(v)})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[5,4,3,2,1].map(r => <SelectItem key={r} value={String(r)}>{r} Stars</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm text-[#78716C] mb-2 block">Source</Label>
                      <Select value={newReview.source} onValueChange={(v) => setNewReview({...newReview, source: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Direct">Direct</SelectItem>
                          <SelectItem value="Airbnb">Airbnb</SelectItem>
                          <SelectItem value="VRBO">VRBO</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-[#78716C] mb-2 block">Review Text</Label>
                    <Textarea value={newReview.text} onChange={(e) => setNewReview({...newReview, text: e.target.value})} placeholder="Write the review..." rows={4} />
                  </div>
                  <Button onClick={handleAddReview} className="w-full bg-[#134E4A] hover:bg-[#0f3d3a]">
                    <Plus size={16} className="mr-2" /> Add Review
                  </Button>
                </div>
              </div>
              <div className="bg-white border border-stone-200 p-6">
                <h2 className="font-serif text-xl text-[#1C1917] mb-6">Manage Reviews</h2>
                {reviews.length === 0 ? (
                  <p className="text-[#78716C]">No reviews yet. Add some from your Airbnb/VRBO listings!</p>
                ) : (
                  <div className="space-y-4 max-h-[500px] overflow-y-auto">
                    {reviews.map((review) => (
                      <div key={review.id} className="p-4 border border-stone-100 bg-stone-50">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-[#1C1917]">{review.guest_name}</p>
                            <p className="text-xs text-[#78716C]">{review.date} • {review.source}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex">
                              {[1,2,3,4,5].map(s => (
                                <Star key={s} size={12} className={s <= review.rating ? "fill-[#D97706] text-[#D97706]" : "text-stone-300"} />
                              ))}
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteReview(review.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1">
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm text-[#78716C] line-clamp-3">{review.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* FAQs Tab */}
          {activeTab === "faqs" && (
            <div data-testid="faqs-content" className="grid lg:grid-cols-2 gap-8">
              <div className="bg-white border border-stone-200 p-6">
                <h2 className="font-serif text-xl text-[#1C1917] mb-6">
                  {editingFaq ? "Edit FAQ" : "Add New FAQ"}
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-[#78716C] mb-2 block">Category</Label>
                    <select
                      value={editingFaq ? editingFaq.category || "General" : newFaq.category}
                      onChange={(e) => editingFaq 
                        ? setEditingFaq({...editingFaq, category: e.target.value})
                        : setNewFaq({...newFaq, category: e.target.value})
                      }
                      className="w-full border border-stone-200 rounded px-3 py-2 text-sm"
                    >
                      {faqCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm text-[#78716C] mb-2 block">Question</Label>
                    <Input 
                      value={editingFaq ? editingFaq.question : newFaq.question}
                      onChange={(e) => editingFaq 
                        ? setEditingFaq({...editingFaq, question: e.target.value})
                        : setNewFaq({...newFaq, question: e.target.value})
                      }
                      placeholder="What is the check-in time?"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[#78716C] mb-2 block">Answer</Label>
                    <Textarea 
                      value={editingFaq ? editingFaq.answer : newFaq.answer}
                      onChange={(e) => editingFaq 
                        ? setEditingFaq({...editingFaq, answer: e.target.value})
                        : setNewFaq({...newFaq, answer: e.target.value})
                      }
                      placeholder="Check-in time is 4:00 PM..."
                      rows={4}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={async () => {
                        try {
                          if (editingFaq) {
                            await axios.put(
                              `${API}/admin/faqs/${editingFaq.id}?question=${encodeURIComponent(editingFaq.question)}&answer=${encodeURIComponent(editingFaq.answer)}&category=${encodeURIComponent(editingFaq.category || 'General')}`,
                              {},
                              getAuthHeaders()
                            );
                            toast.success("FAQ updated!");
                            setEditingFaq(null);
                          } else {
                            await axios.post(
                              `${API}/admin/faqs?question=${encodeURIComponent(newFaq.question)}&answer=${encodeURIComponent(newFaq.answer)}&category=${encodeURIComponent(newFaq.category)}`,
                              {},
                              getAuthHeaders()
                            );
                            toast.success("FAQ added!");
                            setNewFaq({ question: "", answer: "", category: "General" });
                          }
                          fetchData();
                        } catch (e) {
                          toast.error("Error saving FAQ");
                        }
                      }}
                      className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white"
                      disabled={editingFaq ? !editingFaq.question || !editingFaq.answer : !newFaq.question || !newFaq.answer}
                    >
                      {editingFaq ? "Update FAQ" : "Add FAQ"}
                    </Button>
                    {editingFaq && (
                      <Button
                        variant="outline"
                        onClick={() => setEditingFaq(null)}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="bg-white border border-stone-200 p-6">
                <h2 className="font-serif text-xl text-[#1C1917] mb-6">Current FAQs ({faqs.length})</h2>
                {faqs.length === 0 ? (
                  <p className="text-[#78716C] text-center py-8">No FAQs yet. Add your first FAQ.</p>
                ) : (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {faqs.map((faq, index) => (
                      <div key={faq.id} className="border border-stone-200 p-4">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-xs text-[#134E4A] bg-[#e6f2f1] px-2 py-1 rounded">{faq.category || "General"}</span>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingFaq(faq)}
                              className="text-[#134E4A] hover:text-[#0f3d3a]"
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                if (window.confirm("Delete this FAQ?")) {
                                  try {
                                    await axios.delete(`${API}/admin/faqs/${faq.id}`, getAuthHeaders());
                                    toast.success("FAQ deleted!");
                                    fetchData();
                                  } catch (e) {
                                    toast.error("Error deleting FAQ");
                                  }
                                }
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                        <h4 className="font-medium text-[#1C1917] mb-2">{faq.question}</h4>
                        <p className="text-sm text-[#78716C]">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Email Templates Tab */}
          {activeTab === "emails" && (
            <div data-testid="emails-content" className="space-y-6">
              <div className="bg-white border border-stone-200 p-6">
                <h2 className="font-serif text-xl text-[#1C1917] mb-2">Email Templates</h2>
                <p className="text-sm text-[#78716C] mb-6">
                  Customize the emails sent to guests. Use variables like {"{{guest_name}}"} to personalize content.
                </p>
                
                {emailTemplates.length === 0 ? (
                  <p className="text-[#78716C] text-center py-8">Loading email templates...</p>
                ) : (
                  <div className="space-y-4">
                    {emailTemplates.map((template) => (
                      <div key={template.id} className="border border-stone-200 rounded-lg overflow-hidden">
                        <div 
                          className="p-4 bg-stone-50 flex justify-between items-center cursor-pointer"
                          onClick={() => setEditingTemplate(editingTemplate?.id === template.id ? null : template)}
                        >
                          <div>
                            <h3 className="font-medium text-[#1C1917]">{template.name}</h3>
                            <p className="text-sm text-[#78716C]">{template.description}</p>
                          </div>
                          <Button variant="ghost" size="sm">
                            {editingTemplate?.id === template.id ? "Close" : "Edit"}
                          </Button>
                        </div>
                        
                        {editingTemplate?.id === template.id && (
                          <div className="p-4 border-t border-stone-200">
                            <div className="space-y-4">
                              <div>
                                <Label className="text-sm text-[#78716C] mb-2 block">Subject Line</Label>
                                <Input 
                                  value={editingTemplate.subject}
                                  onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                                />
                              </div>
                              <div>
                                <Label className="text-sm text-[#78716C] mb-2 block">Email Body</Label>
                                <Textarea 
                                  value={editingTemplate.body}
                                  onChange={(e) => setEditingTemplate({...editingTemplate, body: e.target.value})}
                                  rows={15}
                                  className="font-mono text-sm"
                                />
                              </div>
                              <div className="bg-stone-50 p-3 rounded">
                                <p className="text-xs text-[#78716C] font-medium mb-2">Available Variables:</p>
                                <div className="flex flex-wrap gap-2">
                                  {template.variables?.map((v) => (
                                    <code key={v} className="text-xs bg-stone-200 px-2 py-1 rounded">
                                      {`{{${v}}}`}
                                    </code>
                                  ))}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  onClick={async () => {
                                    try {
                                      await axios.put(
                                        `${API}/admin/email-templates/${editingTemplate.id}?subject=${encodeURIComponent(editingTemplate.subject)}&body=${encodeURIComponent(editingTemplate.body)}`,
                                        {},
                                        getAuthHeaders()
                                      );
                                      toast.success("Email template saved!");
                                      fetchData();
                                      setEditingTemplate(null);
                                    } catch (e) {
                                      toast.error("Error saving template");
                                    }
                                  }}
                                  className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white"
                                >
                                  Save Template
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() => setEditingTemplate(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Email Preview Tips */}
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                <h3 className="font-medium text-amber-800 mb-2">Email Personalization Tips</h3>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Use {"{{guest_name}}"} to address guests by name</li>
                  <li>• {"{{check_in}}"} and {"{{check_out}}"} show reservation dates</li>
                  <li>• {"{{portal_link}}"} creates a link to the guest portal</li>
                  <li>• Keep subject lines under 50 characters for best display</li>
                </ul>
              </div>
            </div>
          )}

          {/* Media/Files Tab */}
          {activeTab === "media" && (
            <div data-testid="media-content" className="grid lg:grid-cols-2 gap-8">
              <div className="bg-white border border-stone-200 p-6">
                <h2 className="font-serif text-xl text-[#1C1917] mb-6">Upload Images</h2>
                
                {/* Multi-file Upload */}
                <div className="mb-6 p-6 border-2 border-dashed border-stone-300 rounded-lg hover:border-[#134E4A] transition-colors">
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.webp"
                    multiple
                    onChange={async (e) => {
                      const fileInput = e.target;
                      const files = Array.from(fileInput.files || []);
                      if (files.length === 0) return;
                      
                      let successCount = 0;
                      let errorCount = 0;
                      
                      toast.info(`Uploading ${files.length} image(s)...`);
                      
                      for (const file of files) {
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        try {
                          const res = await axios.post(
                            `${API}/admin/gallery/upload?caption=${encodeURIComponent(newMedia.caption || '')}&category=${encodeURIComponent(newMedia.category || 'general')}`,
                            formData,
                            {
                              ...getAuthHeaders(),
                              headers: {
                                ...getAuthHeaders().headers,
                                'Content-Type': 'multipart/form-data'
                              }
                            }
                          );
                          if (res.data.success) {
                            successCount++;
                          }
                        } catch (err) {
                          errorCount++;
                          console.error(`Error uploading ${file.name}:`, err);
                        }
                      }
                      
                      if (successCount > 0) {
                        toast.success(`Successfully uploaded ${successCount} image(s)!`);
                        setNewMedia({ url: "", caption: "", category: "general" });
                        fetchData();
                      }
                      if (errorCount > 0) {
                        toast.error(`Failed to upload ${errorCount} image(s)`);
                      }
                      fileInput.value = ''; // Reset input
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-3 file:px-6 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-[#134E4A] file:text-white hover:file:bg-[#0f3d3a] cursor-pointer"
                  />
                  <p className="text-sm text-[#134E4A] mt-3 font-medium">📸 Select multiple images at once!</p>
                  <p className="text-xs text-[#78716C] mt-1">Hold Ctrl/Cmd while clicking to select multiple files. Supported: JPG, PNG, GIF, WEBP</p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-[#78716C] mb-2 block">Caption (optional - applies to all uploads)</Label>
                    <Input 
                      value={newMedia.caption} 
                      onChange={(e) => setNewMedia({...newMedia, caption: e.target.value})} 
                      placeholder="e.g., Upstairs bedroom with ocean view" 
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[#78716C] mb-2 block">Category</Label>
                    <Select value={newMedia.category} onValueChange={(v) => setNewMedia({...newMedia, category: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="rooms">Rooms</SelectItem>
                        <SelectItem value="exterior">Exterior</SelectItem>
                        <SelectItem value="amenities">Amenities</SelectItem>
                        <SelectItem value="island">Island & Beaches</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Or Add by URL */}
                <div className="mt-6 pt-6 border-t border-stone-200">
                  <h3 className="text-sm font-medium text-[#1C1917] mb-4">Or Add by URL</h3>
                  <div className="space-y-4">
                    <Input 
                      value={newMedia.url} 
                      onChange={(e) => setNewMedia({...newMedia, url: e.target.value})} 
                      placeholder="https://example.com/image.jpg" 
                    />
                    <Button 
                      onClick={handleAddMedia} 
                      className="w-full bg-[#134E4A] hover:bg-[#0f3d3a]"
                      disabled={!newMedia.url}
                    >
                      <Plus size={16} className="mr-2" /> Add from URL
                    </Button>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-stone-200 p-6">
                <h2 className="font-serif text-xl text-[#1C1917] mb-6">Media Library ({gallery.length})</h2>
                {gallery.length === 0 ? (
                  <p className="text-[#78716C]">No images yet. Upload your first image above!</p>
                ) : (
                  <div className="grid grid-cols-2 gap-4 max-h-[600px] overflow-y-auto">
                    {gallery.map((item) => (
                      <div key={item.id} className="relative group border border-stone-100">
                        {item.url.toLowerCase().endsWith('.pdf') ? (
                          <div className="w-full h-32 bg-red-100 flex items-center justify-center">
                            <span className="text-red-600 font-bold">PDF</span>
                          </div>
                        ) : (
                          <img 
                            src={item.url} 
                            alt={item.caption || item.alt_text || 'Gallery image'} 
                            className="w-full h-32 object-cover"
                            onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128"><rect fill="%23f5f5f4" width="128" height="128"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%2378716c" font-size="12">Image</text></svg>'; }}
                          />
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteMedia(item.id)} 
                            className="text-white hover:text-red-400 hover:bg-transparent"
                          >
                            <Trash2 size={20} />
                          </Button>
                        </div>
                        {(item.caption || item.category) && (
                          <div className="p-2">
                            {item.caption && <p className="text-xs text-[#1C1917] truncate">{item.caption}</p>}
                            {item.category && <p className="text-xs text-[#78716C]">{item.category}</p>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Add-ons Tab */}
          {activeTab === "addons" && (
            <div className="space-y-6">
              <div className="bg-white border border-stone-200 p-6">
                <h2 className="font-serif text-xl text-[#1C1917] mb-4">Add New Add-on</h2>
                <div className="grid md:grid-cols-4 gap-4">
                  <div>
                    <Label className="mb-2 block">Name</Label>
                    <Input
                      placeholder="e.g., Golf Cart Rental"
                      value={newAddon.name}
                      onChange={(e) => setNewAddon({ ...newAddon, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block">Price ($)</Label>
                    <Input
                      type="number"
                      placeholder="75"
                      value={newAddon.price}
                      onChange={(e) => setNewAddon({ ...newAddon, price: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block">Type</Label>
                    <Select value={newAddon.type} onValueChange={(v) => setNewAddon({ ...newAddon, type: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="early_checkin">Early Check-in</SelectItem>
                        <SelectItem value="late_checkout">Late Check-out</SelectItem>
                        <SelectItem value="equipment">Equipment Rental</SelectItem>
                        <SelectItem value="service">Service</SelectItem>
                        <SelectItem value="fee">Fee</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleAddAddon} className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white w-full">
                      <Plus size={16} className="mr-2" /> Add
                    </Button>
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="mb-2 block">Description</Label>
                  <Input
                    placeholder="Brief description of the add-on"
                    value={newAddon.description}
                    onChange={(e) => setNewAddon({ ...newAddon, description: e.target.value })}
                  />
                </div>
              </div>

              <div className="bg-white border border-stone-200 p-6">
                <h2 className="font-serif text-xl text-[#1C1917] mb-4">Current Add-ons</h2>
                {addons.length === 0 ? (
                  <p className="text-[#78716C]">No add-ons configured yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Order</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {addons.map((addon, index) => (
                        <TableRow key={addon.id}>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => handleMoveAddon(index, 'up')}
                                disabled={index === 0}
                              >
                                <ChevronUp size={14} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => handleMoveAddon(index, 'down')}
                                disabled={index === addons.length - 1}
                              >
                                <ChevronDown size={14} />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{addon.name}</TableCell>
                          <TableCell>${addon.price}</TableCell>
                          <TableCell className="capitalize">{addon.type?.replace('_', ' ')}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 text-xs ${addon.active !== false ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>
                              {addon.active !== false ? 'Active' : 'Inactive'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleToggleAddon(addon.id, addon.active !== false)}
                              >
                                {addon.active !== false ? 'Deactivate' : 'Activate'}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteAddon(addon.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          )}

          {/* House Rules Tab */}
          {activeTab === "houserules" && (
            <div className="space-y-6">
              <div className="bg-white border border-stone-200 p-6">
                <h2 className="font-serif text-xl text-[#1C1917] mb-4">Add New Rule</h2>
                <div className="flex gap-4">
                  <Input
                    placeholder="e.g., No smoking inside the property"
                    value={newRule}
                    onChange={(e) => setNewRule(e.target.value)}
                    className="flex-1"
                  />
                  <Button onClick={handleAddRule} className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white">
                    <Plus size={16} className="mr-2" /> Add Rule
                  </Button>
                </div>
              </div>

              <div className="bg-white border border-stone-200 p-6">
                <h2 className="font-serif text-xl text-[#1C1917] mb-4">Current House Rules</h2>
                <p className="text-sm text-[#78716C] mb-4">These rules will be displayed in the rental agreement that guests must sign before booking.</p>
                {houseRules.length === 0 ? (
                  <p className="text-[#78716C]">No house rules configured yet.</p>
                ) : (
                  <div className="space-y-2">
                    {houseRules.map((rule, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-stone-50 border border-stone-100">
                        <span className="text-[#1C1917]">{index + 1}. {rule}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteRule(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* iCal Sync Tab */}
          {activeTab === "ical" && (
            <div className="space-y-6">
              <div className="bg-white border border-stone-200 p-6">
                <h2 className="font-serif text-xl text-[#1C1917] mb-4">Add iCal Feed (Airbnb, VRBO)</h2>
                <p className="text-sm text-[#78716C] mb-4">Import bookings from Airbnb, VRBO, or other platforms by adding their iCal export URL.</p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label className="mb-2 block">Platform Name</Label>
                    <Input
                      placeholder="e.g., Airbnb"
                      value={newIcalFeed.name}
                      onChange={(e) => setNewIcalFeed({ ...newIcalFeed, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block">iCal URL</Label>
                    <Input
                      placeholder="https://..."
                      value={newIcalFeed.url}
                      onChange={(e) => setNewIcalFeed({ ...newIcalFeed, url: e.target.value })}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleAddIcalFeed} className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white w-full">
                      <Plus size={16} className="mr-2" /> Add Feed
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-stone-200 p-6">
                <h2 className="font-serif text-xl text-[#1C1917] mb-4">Connected Calendars</h2>
                {icalFeeds.length === 0 ? (
                  <p className="text-[#78716C]">No calendar feeds connected yet. Add Airbnb or VRBO iCal links to sync bookings.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Platform</TableHead>
                        <TableHead>Last Synced</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {icalFeeds.map((feed) => (
                        <TableRow key={feed.id}>
                          <TableCell className="font-medium">{feed.name}</TableCell>
                          <TableCell>{feed.last_synced ? new Date(feed.last_synced).toLocaleString() : 'Never'}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSyncIcalFeed(feed.id)}
                              >
                                Sync Now
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteIcalFeed(feed.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              <div className="bg-amber-50 border border-amber-200 p-4">
                <h3 className="font-medium text-amber-800 mb-2">Export Your Calendar</h3>
                <p className="text-sm text-amber-700 mb-2">Share this iCal URL with Airbnb/VRBO to sync your direct bookings:</p>
                <code className="text-xs bg-white p-2 block rounded border border-amber-200 break-all">
                  {`${process.env.REACT_APP_BACKEND_URL}/api/calendar/ical`}
                </code>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Email Preview Modal */}
      {showEmailPreview && emailPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b border-stone-200 flex justify-between items-center bg-stone-50">
              <div>
                <h2 className="font-serif text-xl text-[#1C1917]">Preview Confirmation Email</h2>
                <p className="text-sm text-[#78716C]">Review before sending to guest</p>
              </div>
              <button 
                onClick={() => setShowEmailPreview(false)}
                className="text-stone-500 hover:text-stone-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            {/* Booking Summary */}
            <div className="p-4 bg-green-50 border-b border-green-200">
              <h3 className="font-medium text-green-800 mb-2">Sending to:</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-green-700">Guest:</span>
                  <span className="ml-2 font-medium">{emailPreview.booking?.guest_name}</span>
                </div>
                <div>
                  <span className="text-green-700">Email:</span>
                  <span className="ml-2 font-medium">{emailPreview.booking?.guest_email}</span>
                </div>
                <div>
                  <span className="text-green-700">Dates:</span>
                  <span className="ml-2 font-medium">{emailPreview.booking?.check_in} to {emailPreview.booking?.check_out}</span>
                </div>
                <div>
                  <span className="text-green-700">Nights:</span>
                  <span className="ml-2 font-medium">{emailPreview.booking?.total_nights}</span>
                </div>
                <div>
                  <span className="text-green-700">Total:</span>
                  <span className="ml-2 font-medium">${emailPreview.booking?.total_price?.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-green-700">Taxes:</span>
                  <span className="ml-2 font-medium">${emailPreview.booking?.taxes?.toLocaleString() || 'Calculated'}</span>
                </div>
              </div>
            </div>
            
            {/* Email Preview */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="mb-2">
                <span className="text-sm text-[#78716C]">Subject:</span>
                <span className="ml-2 font-medium">{emailPreview.subject}</span>
              </div>
              <div className="border border-stone-200 rounded-lg overflow-hidden">
                <iframe
                  srcDoc={emailPreview.html}
                  title="Email Preview"
                  className="w-full h-[400px] border-0"
                />
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t border-stone-200 flex justify-end gap-3 bg-stone-50">
              <Button
                variant="outline"
                onClick={() => setShowEmailPreview(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const bookingId = emailPreview.booking?.id || bookings.find(b => b.guest_email === emailPreview.booking?.guest_email)?.id;
                  if (bookingId) {
                    handleResendConfirmation(bookingId, emailPreview.booking?.guest_email);
                  }
                }}
                className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Email Now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Booking Details Modal */}
      {showBookingDetails && selectedBooking && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b border-stone-200 flex justify-between items-center bg-[#134E4A] text-white">
              <div>
                <h2 className="font-serif text-xl">Booking Details</h2>
                <p className="text-sm text-white/80">Full price breakdown and guest info</p>
              </div>
              <button 
                onClick={() => setShowBookingDetails(false)}
                className="text-white/80 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>
            
            {/* Guest Info */}
            <div className="p-4 bg-stone-50 border-b border-stone-200">
              <h3 className="font-medium text-[#134E4A] mb-3 flex items-center gap-2">
                <span className="text-lg">👤</span> Guest Information
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-[#78716C]">Name:</span>
                  <span className="ml-2 font-medium">{selectedBooking.guest_name}</span>
                </div>
                <div>
                  <span className="text-[#78716C]">Email:</span>
                  <span className="ml-2 font-medium">{selectedBooking.guest_email}</span>
                </div>
                <div>
                  <span className="text-[#78716C]">Phone:</span>
                  <span className="ml-2 font-medium">{selectedBooking.guest_phone}</span>
                </div>
                <div>
                  <span className="text-[#78716C]">Guests:</span>
                  <span className="ml-2 font-medium">{selectedBooking.guest_count}</span>
                </div>
              </div>
              {selectedBooking.special_requests && (
                <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-sm">
                  <span className="text-amber-700 font-medium">Special Requests:</span>
                  <p className="text-amber-800 mt-1">{selectedBooking.special_requests}</p>
                </div>
              )}
            </div>
            
            {/* Booking Dates */}
            <div className="p-4 border-b border-stone-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-[#134E4A] flex items-center gap-2">
                  <span className="text-lg">📅</span> Stay Details
                </h3>
                {!editingDates ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditCheckIn(selectedBooking.check_in);
                      setEditCheckOut(selectedBooking.check_out);
                      setEditingDates(true);
                    }}
                    className="text-xs"
                  >
                    Edit Dates
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingDates(false)}
                      className="text-xs"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleUpdateBookingDates}
                      className="text-xs bg-[#134E4A] hover:bg-[#0f3d3a]"
                    >
                      Save
                    </Button>
                  </div>
                )}
              </div>
              
              {editingDates ? (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-[#78716C]">Check-in:</Label>
                    <Input
                      type="date"
                      value={editCheckIn}
                      onChange={(e) => setEditCheckIn(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[#78716C]">Check-out:</Label>
                    <Input
                      type="date"
                      value={editCheckOut}
                      onChange={(e) => setEditCheckOut(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-[#78716C]">Check-in:</span>
                    <p className="font-medium">{selectedBooking.check_in}</p>
                  </div>
                  <div>
                    <span className="text-[#78716C]">Check-out:</span>
                    <p className="font-medium">{selectedBooking.check_out}</p>
                  </div>
                  <div>
                    <span className="text-[#78716C]">Total Nights:</span>
                    <p className="font-medium">{selectedBooking.total_nights}</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Price Breakdown */}
            <div className="p-4 border-b border-stone-200 flex-1 overflow-y-auto">
              <h3 className="font-medium text-[#134E4A] mb-3 flex items-center gap-2">
                <span className="text-lg">💰</span> Price Breakdown
              </h3>
              <div className="bg-stone-50 rounded-lg p-4">
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b border-stone-200">
                      <td className="py-2 text-[#78716C]">
                        Accommodations ({selectedBooking.total_nights || selectedBooking.nights || 'N/A'} nights × ${
                          selectedBooking.nightly_rate 
                            ? selectedBooking.nightly_rate 
                            : selectedBooking.subtotal && selectedBooking.total_nights 
                              ? Math.round(selectedBooking.subtotal / selectedBooking.total_nights) 
                              : selectedBooking.accommodation_total && selectedBooking.total_nights
                                ? Math.round(selectedBooking.accommodation_total / selectedBooking.total_nights)
                                : 'N/A'
                        }/night)
                      </td>
                      <td className="py-2 text-right font-medium">${
                        selectedBooking.subtotal?.toLocaleString() 
                        || selectedBooking.accommodation_total?.toLocaleString() 
                        || (selectedBooking.nightly_rate && selectedBooking.total_nights 
                            ? (selectedBooking.nightly_rate * selectedBooking.total_nights).toLocaleString() 
                            : 'N/A')
                      }</td>
                    </tr>
                    <tr className="border-b border-stone-200">
                      <td className="py-2 text-[#78716C]">Cleaning Fee</td>
                      <td className="py-2 text-right font-medium">${selectedBooking.cleaning_fee?.toLocaleString() || '350'}</td>
                    </tr>
                    <tr className="border-b border-stone-200">
                      <td className="py-2 text-[#78716C]">Golf Cart Gas Fee</td>
                      <td className="py-2 text-right font-medium">${selectedBooking.golf_cart_gas_fee?.toLocaleString() || '50'}</td>
                    </tr>
                    {selectedBooking.addons_total > 0 && (
                      <tr className="border-b border-stone-200">
                        <td className="py-2 text-[#78716C]">Add-ons</td>
                        <td className="py-2 text-right font-medium">${selectedBooking.addons_total?.toLocaleString()}</td>
                      </tr>
                    )}
                    {selectedBooking.discount_amount > 0 && (
                      <tr className="border-b border-stone-200 text-green-600">
                        <td className="py-2">Discount</td>
                        <td className="py-2 text-right font-medium">-${selectedBooking.discount_amount?.toLocaleString()}</td>
                      </tr>
                    )}
                    <tr className="border-b border-stone-200">
                      <td className="py-2 text-[#78716C]">Maine Rental Tax (9%)</td>
                      <td className="py-2 text-right font-medium">${
                        selectedBooking.taxes?.toLocaleString() 
                        || (selectedBooking.total_price && selectedBooking.total_amount 
                            ? Math.round(selectedBooking.total_price * 0.09 / 1.09).toLocaleString()
                            : 'N/A')
                      }</td>
                    </tr>
                    <tr className="border-t-2 border-[#134E4A]">
                      <td className="py-3 font-semibold text-[#134E4A]">Trip Total</td>
                      <td className="py-3 text-right font-bold text-lg text-[#134E4A]">${selectedBooking.total_price?.toLocaleString()}</td>
                    </tr>
                    <tr className="bg-amber-50 -mx-4">
                      <td className="py-2 text-amber-700">Security Deposit (HOLD only)</td>
                      <td className="py-2 text-right font-medium text-amber-700">${selectedBooking.protection_amount?.toLocaleString() || '400'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {/* Fix Pricing Button - show if pricing data is missing */}
              {(!selectedBooking.subtotal && !selectedBooking.nightly_rate) && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 mb-2">
                    <strong>Notice:</strong> This booking is missing pricing data.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-400 text-amber-700 hover:bg-amber-100"
                    onClick={() => {
                      setSelectedBooking(null);
                      openFixPricing(selectedBooking);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Fix Pricing
                  </Button>
                </div>
              )}
              
              {/* Selected Add-ons */}
              {selectedBooking.selected_addons && selectedBooking.selected_addons.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-[#78716C] mb-2">Selected Add-ons:</h4>
                  <ul className="text-sm space-y-1">
                    {selectedBooking.selected_addons.map((addon, i) => (
                      <li key={i} className="flex justify-between">
                        <span>{addon.name || addon}</span>
                        <span className="font-medium">${addon.total?.toLocaleString() || addon.price?.toLocaleString() || 'N/A'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            {/* Payment Status */}
            <div className="p-4 border-b border-stone-200 bg-stone-50">
              <h3 className="font-medium text-[#134E4A] mb-3 flex items-center gap-2">
                <span className="text-lg">💳</span> Payment Status
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-[#78716C]">Status:</span>
                  <span className={`ml-2 px-2 py-1 text-xs rounded ${
                    selectedBooking.payment_status === "paid" ? "bg-green-200 text-green-800" : 
                    selectedBooking.payment_status === "deposit_paid" ? "bg-blue-100 text-blue-800" : 
                    selectedBooking.payment_status === "refunded" ? "bg-red-100 text-red-700 font-semibold" :
                    "bg-amber-100 text-amber-800"
                  }`}>
                    {selectedBooking.payment_status}
                  </span>
                </div>
                <div>
                  <span className="text-[#78716C]">Amount Paid:</span>
                  <span className="ml-2 font-medium text-green-700">${(selectedBooking.amount_paid || selectedBooking.first_payment || 0).toLocaleString()}</span>
                </div>
                {selectedBooking.second_payment > 0 && (
                  <>
                    <div>
                      <span className="text-[#78716C]">2nd Payment:</span>
                      <span className="ml-2 font-medium">${selectedBooking.second_payment?.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-[#78716C]">Due:</span>
                      <span className="ml-2 font-medium">{selectedBooking.second_payment_due || 'TBD'}</span>
                    </div>
                  </>
                )}
                {selectedBooking.third_payment > 0 && (
                  <>
                    <div>
                      <span className="text-[#78716C]">3rd Payment:</span>
                      <span className="ml-2 font-medium">${selectedBooking.third_payment?.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-[#78716C]">Due:</span>
                      <span className="ml-2 font-medium">{selectedBooking.third_payment_due || 'TBD'}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Extra Charges & Security Deposit */}
            <ChargesPanel booking={selectedBooking} />

            {/* Modal Footer */}
            <div className="p-4 border-t border-stone-200 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowBookingDetails(false)}
              >
                Close
              </Button>
              {selectedBooking.payment_status !== "refunded" && (
                <Button
                  variant="outline"
                  onClick={() => handleMarkAsRefunded(selectedBooking.id)}
                  className="border-red-600 text-red-600 hover:bg-red-50"
                >
                  Mark as Refunded
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  window.open(`${API}/bookings/${selectedBooking.id}/receipt`, '_blank');
                }}
                className="border-[#134E4A] text-[#134E4A] hover:bg-[#134E4A]/10"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
              <Button
                onClick={() => {
                  setShowBookingDetails(false);
                  handlePreviewEmail(selectedBooking.id);
                }}
                className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Confirmation
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Fix Pricing Modal */}
      {showFixPricing && fixPricingBooking && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-lg w-full overflow-hidden">
            {/* Modal Header */}
            <div className="p-4 border-b border-stone-200 bg-amber-50">
              <h2 className="font-serif text-xl text-amber-800">Fix Booking Pricing</h2>
              <p className="text-sm text-amber-700">Correct the nightly rate for this booking</p>
            </div>
            
            {/* Current Booking Info */}
            <div className="p-4 border-b border-stone-100">
              <h3 className="font-medium mb-2">Current Booking:</h3>
              <div className="text-sm space-y-1">
                <p><span className="text-[#78716C]">Guest:</span> {fixPricingBooking.guest_name}</p>
                <p><span className="text-[#78716C]">Dates:</span> {fixPricingBooking.check_in} to {fixPricingBooking.check_out}</p>
                <p><span className="text-[#78716C]">Nights:</span> {fixPricingBooking.total_nights}</p>
                <p><span className="text-[#78716C]">Current Rate:</span> <span className="text-red-600">${fixPricingBooking.nightly_rate || 'Unknown'}/night</span></p>
                <p><span className="text-[#78716C]">Current Total:</span> ${fixPricingBooking.total_price?.toLocaleString()}</p>
              </div>
            </div>
            
            {/* Rate Guide */}
            <div className="p-4 bg-blue-50 border-b border-blue-100">
              <h4 className="font-medium text-blue-800 mb-2">Rate Guide:</h4>
              <div className="text-xs text-blue-700 space-y-1">
                <p>• Oct-May: $800/night (7+ nights) → $1,150/night (4 nights)</p>
                <p>• June & Sept: $850/night (7+ nights) → $1,200/night (4 nights)</p>
                <p>• July & Aug: $900/night (7+ nights) → <strong>$1,150/night (4 nights)</strong></p>
              </div>
            </div>
            
            {/* New Rate Input */}
            <div className="p-4">
              <label className="block text-sm font-medium mb-2">Correct Nightly Rate ($)</label>
              <Input
                type="number"
                value={newNightlyRate}
                onChange={(e) => setNewNightlyRate(e.target.value)}
                placeholder="e.g., 1150"
                className="mb-2"
              />
              {newNightlyRate && fixPricingBooking.total_nights && (
                <div className="text-sm bg-green-50 p-3 rounded border border-green-200">
                  <p className="font-medium text-green-800">New Calculation:</p>
                  <p>{fixPricingBooking.total_nights} nights × ${newNightlyRate} = ${(fixPricingBooking.total_nights * parseFloat(newNightlyRate)).toLocaleString()}</p>
                  <p className="text-xs text-green-600 mt-1">+ cleaning fee + add-ons + 9% tax</p>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t border-stone-200 flex justify-end gap-3 bg-stone-50">
              <Button
                variant="outline"
                onClick={() => { setShowFixPricing(false); setFixPricingBooking(null); setNewNightlyRate(""); }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleFixPricing}
                disabled={!newNightlyRate}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                Update Pricing
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Payment Modal */}
      {showApproveModal && selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-200">
              <h2 className="text-2xl font-serif text-[#1C1917]">Approve Payment Submission</h2>
              <p className="text-sm text-[#78716C] mt-1">Review and confirm payment approval</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-medium text-green-800 mb-2">✓ Ready to Approve</h3>
                <p className="text-sm text-green-700">
                  This will update the booking payment status and send a confirmation email to the guest.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#78716C]">Guest</Label>
                  <p className="font-medium">{selectedSubmission.guest_name}</p>
                </div>
                <div>
                  <Label className="text-[#78716C]">Amount</Label>
                  <p className="font-medium text-lg">${selectedSubmission.amount.toFixed(2)}</p>
                </div>
                <div>
                  <Label className="text-[#78716C]">Payment Type</Label>
                  <p className="font-medium capitalize">{selectedSubmission.payment_type}</p>
                </div>
                <div>
                  <Label className="text-[#78716C]">Method</Label>
                  <p className="font-medium capitalize">{selectedSubmission.payment_method}</p>
                </div>
              </div>
              
              <div>
                <Label htmlFor="admin-notes-approve">Admin Notes (Optional)</Label>
                <Textarea
                  id="admin-notes-approve"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Internal notes about this approval..."
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-stone-200 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowApproveModal(false);
                  setSelectedSubmission(null);
                  setAdminNotes("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleApprovePayment}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve & Send Email
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Payment Modal */}
      {showRejectModal && selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-200">
              <h2 className="text-2xl font-serif text-[#1C1917]">Reject Payment Submission</h2>
              <p className="text-sm text-[#78716C] mt-1">Provide a reason - guest will be notified</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-medium text-red-800 mb-2">⚠️ Payment will be rejected</h3>
                <p className="text-sm text-red-700">
                  The guest will receive an email explaining why their payment was not accepted.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#78716C]">Guest</Label>
                  <p className="font-medium">{selectedSubmission.guest_name}</p>
                </div>
                <div>
                  <Label className="text-[#78716C]">Amount</Label>
                  <p className="font-medium text-lg">${selectedSubmission.amount.toFixed(2)}</p>
                </div>
              </div>
              
              <div>
                <Label htmlFor="rejection-reason" className="text-red-700">
                  Rejection Reason (Required) *
                </Label>
                <Textarea
                  id="rejection-reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="E.g., Amount does not match expected payment of $2,500.00"
                  className="mt-1 border-red-200 focus:border-red-400"
                  rows={3}
                  required
                />
                <p className="text-xs text-[#78716C] mt-1">
                  This message will be sent to the guest
                </p>
              </div>
              
              <div>
                <Label htmlFor="admin-notes-reject">Admin Notes (Internal Only)</Label>
                <Textarea
                  id="admin-notes-reject"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Internal notes (not sent to guest)..."
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-stone-200 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedSubmission(null);
                  setRejectionReason("");
                  setAdminNotes("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRejectPayment}
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={!rejectionReason}
              >
                <X className="w-4 h-4 mr-2" />
                Reject & Notify Guest
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
}
